import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import * as React from 'react';
import React__default from 'react';
import classNames from 'classnames';
import { ExclamationCircle, InfoCircle, CheckCircle, Warning, Close, ArrowDown, ArrowUp, ExclamationCircleFill, StarFill, StarHalf, Star, CaretDown } from '@walmart-web/livingdesign-icons';
import { CSSTransition } from 'react-transition-group';
import { useSSRSafeId, SSRProvider } from '@react-aria/ssr';
import * as ReactDOM from 'react-dom';
import FocusLock from 'react-focus-lock';
import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

var style$1a = {"visuallyHidden":"VisuallyHidden-module_visuallyHidden__1mhX_"};

/**
 * Assert state which your program assumes to be true.
 *
 * @private
 * @param {boolean} condition State to assert.
 * @param {string} message Message to be thrown when not in production.
 * @returns {undefined}
 */
const invariant = (condition, message) => {
  if (condition) {
    return;
  }

  const error = `LD: ${message || "unknown violation"}`;

  if (process.env.NODE_ENV === "production") {
    /* eslint-disable-next-line no-console */
    console.error(error);
  } else {
    throw new Error(error);
  }
};

/**
 * Tranform common props to properties for use with a HTMLElement.
 *
 * @todo Deprecate `className` and `style` in the next major release.
 */

/* eslint-disable-next-line @typescript-eslint/ban-types */

const applyCommonProps = (props,
/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
Component, isWarnDisabled = false, isAriaLabelBanned = false) => {
  const componentName = Component.displayName || Component.name || "Unknown";
  /* istanbul ignore if */

  if (process.env.NODE_ENV !== "production") {
    if (!isWarnDisabled) {
      if (props.className) {
        /* eslint-disable-next-line no-console */
        console.warn(`${componentName}.className property will be deprecated in the next major version. Please use the "UNSAFE_className" property.`);
      }

      if (props.style) {
        /* eslint-disable-next-line no-console */
        console.warn(`${componentName}.style property will be deprecated in the next major version. Please use the "UNSAFE_style" property.`);
      }
      /* eslint-enable no-console */

    }
  }

  if (isAriaLabelBanned) {
    invariant(props["aria-label"] === undefined, `\`${componentName}\` accessibility violation. \`${componentName}\` is banned from using \`aria-label\`.`);
  }

  const appliedProps = {};

  for (const key in props) {
    if (props.hasOwnProperty(key)) {
      const value = props[key];

      if (key === "UNSAFE_className" || key === "className") {
        /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
        // @ts-ignore
        appliedProps.className = classNames(appliedProps.className, value);
      } else if (key === "UNSAFE_style" || key === "style") {
        /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
        // @ts-ignore
        appliedProps.style = appliedProps.style ? Object.assign(Object.assign({}, appliedProps.style), value) : value;
      } else {
        appliedProps[key] = value;
      }
    }
  }

  return appliedProps;
};

/**
 * A utility component that makes content available for screen readers only.
 *
 * @see {@link https://livingdesign.walmart.com/utilities/visually-hidden/react/}
 */

const VisuallyHidden = props => {
  const _a = applyCommonProps(props, VisuallyHidden),
        {
    className
  } = _a,
        rest = __rest(_a, ["className"]);

  return jsx("span", Object.assign({
    className: classNames(style$1a.visuallyHidden, className)
  }, rest), void 0);
};
VisuallyHidden.displayName = "VisuallyHidden";

const A11yAnnouncementContext = React.createContext({
  announceAssertive:
  /* istanbul ignore next */
  () => undefined,
  announcePolite:
  /* istanbul ignore next */
  () => undefined
});
const useA11yAnnouncement = () => React.useContext(A11yAnnouncementContext);
/**
 * A utility component that allows messages to be programmatically broadcast to assistive technologies.
 *
 * @see {@link https://livingdesign.walmart.com/utilities/a11y-announcement/react/}
 */

const A11yAnnouncementProvider = props => {
  const {
    children
  } = props;
  const [assertive, announceAssertive] = React.useState();
  const [polite, announcePolite] = React.useState();
  const contextValue = React.useRef({
    announceAssertive,
    announcePolite
  });
  return jsxs(A11yAnnouncementContext.Provider, Object.assign({
    value: contextValue.current
  }, {
    children: [children, jsx(VisuallyHidden, Object.assign({
      "aria-live": "assertive"
    }, {
      children: assertive
    }), void 0), jsx(VisuallyHidden, Object.assign({
      "aria-live": "polite"
    }, {
      children: polite
    }), void 0)]
  }), void 0);
};
A11yAnnouncementProvider.displayName = "A11yAnnouncementProvider";

var style$19 = {"alert":"Alert-module_alert__1Wt7d","leading":"Alert-module_leading__369RZ","content":"Alert-module_content__1Vy5h","contentContainer":"Alert-module_contentContainer__U-5Wu","error":"Alert-module_error__12p80","info":"Alert-module_info__1Vhos","success":"Alert-module_success__I2EH_","warning":"Alert-module_warning__2hhUG"};

var style$18 = {"action":"AlertActionButton-module_action__V8qrq"};

/**
 * Get secure anchor props given a `target`.
 * @private
 *
 * @param {string} [target] The anchor's `target`
 * @returns {Object} The secure anchor props
 */
const getTarget = target => {
  if (target === "") {
    /* istanbul ignore next */
    return {};
  }
  /**
   * @see {@link https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a#Security_and_privacy_concerns}
   */


  if (target === "_blank") {
    /* istanbul ignore next */
    return {
      target,
      rel: "noopener noreferrer"
    };
  }

  return {
    target
  };
};

/**
 * Looks like a button should act like a button.
 *
 * @see {@link https://jira.walmart.com/browse/LD-374?focusedCommentId=12802856&page=com.atlassian.jira.plugin.system.issuetabpanels%3Acomment-tabpanel#comment-12802856}
 * @param anchorProps
 * @private
 */
const onAnchorKeyDown = anchorProps => {
  const {
    href,
    onClick,
    onKeyDown
  } = anchorProps;
  return event => {
    onKeyDown === null || onKeyDown === void 0 ? void 0 : onKeyDown(event);

    if (event.defaultPrevented || event.key !== " ") {
      return;
    }

    onClick === null || onClick === void 0 ? void 0 : onClick(event); // anchorProps.onClick could call preventDefault
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition

    if (event.defaultPrevented || !href) {
      return;
    }

    location.assign(href);
  };
};

const isAnchor$2 = props => "href" in props;
/**
 * @private
 */


const AlertActionButton = React.forwardRef((props, ref) => {
  const _a = applyCommonProps(props, AlertActionButton),
        {
    className
  } = _a,
        rest = __rest(_a, ["className"]);

  const classes = classNames(style$18.action, className);

  if (isAnchor$2(props)) {
    const _b = rest,
          {
      children,
      href,
      onClick,
      onKeyDown: anchorOnKeyDown,
      target
    } = _b,
          anchorProps = __rest(_b, ["children", "href", "onClick", "onKeyDown", "target"]); // Looks like a button should act like a button.
    //
    // @see {@link https://jira.walmart.com/browse/LD-374?focusedCommentId=12802856&page=com.atlassian.jira.plugin.system.issuetabpanels%3Acomment-tabpanel#comment-12802856}


    const onKeyDown = onAnchorKeyDown({
      href,
      onClick,
      onKeyDown: anchorOnKeyDown
    });
    return jsx("a", Object.assign({
      className: classes,
      href: href,
      onClick: onClick,
      onKeyDown: onKeyDown,
      ref: ref
    }, getTarget(target), anchorProps, {
      children: children
    }), void 0);
  }

  const buttonProps = __rest(rest, []);

  return jsx("button", Object.assign({
    className: classes,
    ref: ref,
    type: "button"
  }, buttonProps), void 0);
});
AlertActionButton.displayName = "AlertActionButton";

/**
 * Alerts provide brief information and feedback to a user.
 *
 * @see {@link https://livingdesign.walmart.com/components/alert/react/}
 */

const Alert = props => {
  const _a = applyCommonProps(props, Alert, true),
        {
    actionButtonProps,
    children,
    className,
    variant = "success"
  } = _a,
        rest = __rest(_a, ["actionButtonProps", "children", "className", "variant"]);

  return jsxs("div", Object.assign({
    className: classNames(style$19.alert, variant === "error" && style$19.error, variant === "info" && style$19.info, variant === "success" && style$19.success, variant === "warning" && style$19.warning, className)
  }, rest, {
    children: [jsxs("span", Object.assign({
      className: style$19.leading
    }, {
      children: [variant === "error" && jsx(ExclamationCircle, {}, void 0), variant === "info" && jsx(InfoCircle, {}, void 0), variant === "success" && jsx(CheckCircle, {}, void 0), variant === "warning" && jsx(Warning, {}, void 0)]
    }), void 0), jsxs("span", Object.assign({
      className: style$19.contentContainer
    }, {
      children: [jsx("span", Object.assign({
        className: style$19.content
      }, {
        children: children
      }), void 0), actionButtonProps && jsx(AlertActionButton, Object.assign({}, actionButtonProps), void 0)]
    }), void 0)]
  }), void 0);
};
Alert.displayName = "Alert";

var style$17 = {"badge":"Badge-module_badge__176k3","blue":"Badge-module_blue__31KPc","gray":"Badge-module_gray__2Y1Tw","green":"Badge-module_green__35fCR","purple":"Badge-module_purple__e-1Zn","red":"Badge-module_red__E-het","spark":"Badge-module_spark__3vnZd","white":"Badge-module_white__3jMWs","hasTextLabel":"Badge-module_hasTextLabel__D_zwY"};

/**
 * Badges are used to draw a customer’s focus to item count. Items may be
 * products, messages, or something else. They are visual only and
 * non-interactive.
 *
 * @see {@link https://livingdesign.walmart.com/components/badge/react/}
 */

const Badge = props => {
  const _a = applyCommonProps(props, Badge),
        {
    children,
    color = "blue",
    className
  } = _a,
        rest = __rest(_a, ["children", "color", "className"]);

  const hasTextLabel = typeof children !== "boolean" && children !== null && typeof children !== "undefined";
  return jsx("span", Object.assign({
    className: classNames(style$17.badge, hasTextLabel && style$17.hasTextLabel, color === "blue" && style$17.blue, color === "gray" && style$17.gray, color === "green" && style$17.green, color === "purple" && style$17.purple, color === "red" && style$17.red, color === "spark" && style$17.spark, color === "white" && style$17.white, className)
  }, rest, {
    children: children
  }), void 0);
};
Badge.displayName = "Badge";

var style$16 = {"banner":"Banner-module_banner__2kBVl","contentContainer":"Banner-module_contentContainer__3ZjQl","content":"Banner-module_content__1HZaa","error":"Banner-module_error__2OAb7","info":"Banner-module_info__32pPU","success":"Banner-module_success__kHO1h","warning":"Banner-module_warning__aJAJ9"};

var style$15 = {"close":"BannerCloseButton-module_close__1v5MC","closeIcon":"BannerCloseButton-module_closeIcon__1KltS"};

/**
 * @private
 */

const BannerCloseButton = React.forwardRef((props, ref) => {
  const _a = applyCommonProps(props, BannerCloseButton, true),
        {
    children,
    className
  } = _a,
        rest = __rest(_a, ["children", "className"]);

  return jsx("button", Object.assign({
    className: classNames(style$15.close, className),
    ref: ref,
    type: "button"
  }, rest, {
    children: jsx(Close, {
      className: style$15.closeIcon
    }, void 0)
  }), void 0);
});

/**
 * Banners provide brief information about a significant incident affecting large numbers (or all) of the population.
 *
 * @see {@link https://livingdesign.walmart.com/components/banner/react/}
 */

const Banner = props => {
  const _a = applyCommonProps(props, Banner, true),
        {
    children,
    className,
    closeButtonProps,
    onClose,
    variant = "info"
  } = _a,
        rest = __rest(_a, ["children", "className", "closeButtonProps", "onClose", "variant"]);

  return jsxs("div", Object.assign({
    className: classNames(style$16.banner, variant === "error" && style$16.error, variant === "info" && style$16.info, variant === "success" && style$16.success, variant === "warning" && style$16.warning, className),
    role: "alert"
  }, rest, {
    children: [jsx("div", Object.assign({
      className: style$16.contentContainer
    }, {
      children: jsx("div", Object.assign({
        className: style$16.content
      }, {
        children: children
      }), void 0)
    }), void 0), jsx(BannerCloseButton, Object.assign({
      "aria-label": "Close banner"
    }, closeButtonProps, {
      onClick: event => {
        var _a;

        (_a = closeButtonProps === null || closeButtonProps === void 0 ? void 0 : closeButtonProps.onClick) === null || _a === void 0 ? void 0 : _a.call(closeButtonProps, event);
        onClose(event);
      }
    }), void 0)]
  }), void 0);
};
Banner.displayName = "Banner";

var style$14 = {"container":"BottomSheet-module_container__kVykb","bottomSheet":"BottomSheet-module_bottomSheet__2JfD6","transitionEnter":"BottomSheet-module_transitionEnter__3P6Z_","transitionEnterActive":"BottomSheet-module_transitionEnterActive__1Bwlv","transitionExit":"BottomSheet-module_transitionExit__smSoK","transitionExitActive":"BottomSheet-module_transitionExitActive__2fbuY","header":"BottomSheet-module_header__2SsRa","title":"BottomSheet-module_title__8XLro","content":"BottomSheet-module_content__1dXvg","contentInner":"BottomSheet-module_contentInner__1TUPM","actions":"BottomSheet-module_actions__2tk8k","scrim":"BottomSheet-module_scrim__wN02S"};

var style$13 = {"container":"Overlay-module_container__2GrY9","trap":"Overlay-module_trap__RzaQD"};

/**
 * A utility component that isolates browser focus for accessibility purposes.
 *
 * @see {@link https://livingdesign.walmart.com/utilities/focus-trap/react/}
 */

const FocusTrap = props => {
  const _a = applyCommonProps(props, FocusTrap, true),
        {
    hasFocusReturn = true
  } = _a,
        rest = __rest(_a, ["hasFocusReturn"]); // Focus return conflicts with all UI announcements.
  //
  // @see {@link https://jira.walmart.com/browse/LD-607}
  //
  // 1. Focus return will stomp over aria-live. Screen reader can only read one thing at a time.
  // 2. Focus return is required for all modal-like components.
  // 3. Recommend manage all UI announcement within children or outside experience entirely.


  return jsx(FocusLock, Object.assign({
    returnFocus: hasFocusReturn
  }, rest), void 0);
};
FocusTrap.displayName = "FocusTrap";

/**
 * Create and attach element to the body to be used for portal.
 * @private
 *
 * @see {@link https://www.jayfreestone.com/writing/react-portals-with-hooks}
 *
 * @param {Object} [options] Configuration for the portal
 * @returns {React.Ref} A ref to the portal element
 */

const usePortal = (options = {}) => {
  const {
    a11yHidden = true
  } = options;
  const portalRef = React.useRef(null);
  const container = document.body;

  if (!portalRef.current) {
    portalRef.current = document.createElement("div");
  } // istanbul ignore next


  React.useEffect(() => {
    // istanbul ignore next
    if (!a11yHidden) {
      return () => undefined;
    } // We need to add `aria-hidden` to all children of <body>,
    // except for the modal. We .filter() the list to find only
    // <div>-like elements, ignoring <script>, <style>, etc.
    // We also don't want to affect any elements that may already
    // have `aria-hidden` set to true.


    const bodyChildren = Array.from(container.children).filter(child => child.innerHTML && [null, "false"].includes(child.getAttribute("aria-hidden")));
    bodyChildren.forEach(el => el.setAttribute("aria-hidden", "true"));
    return () => {
      bodyChildren.forEach(el => el.removeAttribute("aria-hidden"));
    };
  }, [a11yHidden, container]);
  React.useEffect(() => {
    container.appendChild(portalRef.current);
    return () => {
      // istanbul ignore next
      if (!portalRef.current) {
        return;
      } // NOTE: IE 11 needs removeChild


      container.removeChild(portalRef.current);
    };
  }, [container, portalRef]);
  return portalRef;
};

/**
 * @private
 */

const Overlay = props => {
  const {
    children,
    className
  } = props,
        rest = __rest(props, ["children", "className"]);

  const overlay = usePortal();
  return ReactDOM.createPortal(jsx("div", Object.assign({
    className: classNames(style$13.container, className)
  }, rest, {
    children: jsx(FocusTrap, Object.assign({
      className: style$13.trap
    }, {
      children: children
    }), void 0)
  }), void 0), overlay.current);
};

var style$12 = {"closeButton":"OverlayCloseButton-module_closeButton__10lV9"};

/**
 * @private
 */

const OverlayCloseButton = React.forwardRef((props, ref) => {
  const _a = applyCommonProps(props, OverlayCloseButton, true),
        {
    children,
    className
  } = _a,
        rest = __rest(_a, ["children", "className"]);

  return jsx("button", Object.assign({
    className: classNames(style$12.closeButton, className),
    ref: ref,
    type: "button"
  }, rest, {
    children: jsx(Close, {
      size: "medium"
    }, void 0)
  }), void 0);
});
OverlayCloseButton.displayName = "OverlayCloseButton";

var style$11 = {"scrim":"OverlayScrim-module_scrim__eT7vN"};

/**
 * @private
 */

const OverlayScrim = props => {
  const {
    className
  } = props,
        rest = __rest(props, ["className"]);

  return jsx("div", Object.assign({
    className: classNames(style$11.scrim, className)
  }, rest), void 0);
};

/* istanbul ignore next */

const allowTouchMove = el => el.tagName === "INPUT" && el.type === "range" || el.tagName === "TEXTAREA";
/**
 * Locks the `body` from scrolling except for provided element.
 * @private
 *
 * @see {@link https://github.com/willmcpo/body-scroll-lock}
 *
 * @example
 * const [setScrollableContent] = useLockBodyScroll();
 *
 * return (
 *  <div ref={setScrollableContent} ....>...</div>
 * );
 */


const useLockBodyScroll = () => {
  const [contentElement, setContentElement] = React.useState(null);
  React.useEffect(() => {
    /**
     * @note On Safari iOS, body-scroll-lock locks scrolling on all elements except for the passed in ref. We need to pass in the element that can scroll, Which is why we pass in our own `allowTouchMove`.
     *
     * In order to do this it swallows touchmove events, which is a problem for child elements that have behavior that depends on touchmove (e.g. range inputs, text areas).
     *
     * @see {@link https://github.com/reakit/reakit/issues/469#issuecomment-549013422}
     * @see {@link https://github.com/willmcpo/body-scroll-lock/blob/fe6bfea3a134f337e77e62ae380cdf29eac7216b/src/bodyScrollLock.js#L52}
     * @see {@link https://github.com/willmcpo/body-scroll-lock/issues/111}
     * @see {@link https://github.com/willmcpo/body-scroll-lock/issues/76}
     */
    if (contentElement) {
      disableBodyScroll(contentElement, {
        allowTouchMove
      });
    }

    return () => clearAllBodyScrollLocks();
  }, [contentElement]);
  return [setContentElement];
};

/**
 * Trigger click event when clicking a given ref.
 * @private
 *
 * @see {@link https://github.com/gragland/usehooks/blob/master/src/pages/useOnClickOutside.md}
 *
 * @example
 * useOnClick(ref1);
 * useOnClick([ref1, ref2]);
 *
 * @param {Function} onClick Click event handler
 * @param {Array} [dependencies=[]] Use effect dependencies
 * @param {boolean} [useCapture=false] Whether to dispatch before any events beneath it in the DOM tree
 * @returns {undefined}
 */

const useOnClick = (onClick, dependencies = [], useCapture = false) => React.useEffect(() => {
  /* istanbul ignore if: JSDOM has open issue for PointerEvent, which is undefined and mocking window.PointerEvent doesn't work properly */
  if (window.PointerEvent) {
    /* istanbul ignore next */
    document.addEventListener("pointerdown", onClick, useCapture);
  } else {
    document.addEventListener("mousedown", onClick, useCapture);
    document.addEventListener("touchstart", onClick, useCapture);
  }

  return () => {
    /* istanbul ignore if: JSDOM has open issue for PointerEvent, which is undefined and mocking window.PointerEvent doesn't work properly */
    if (window.PointerEvent) {
      /* istanbul ignore next */
      document.removeEventListener("pointerdown", onClick, useCapture);
    } else {
      document.removeEventListener("mousedown", onClick, useCapture);
      document.removeEventListener("touchstart", onClick, useCapture);
    }
  };
  /* eslint-disable-next-line react-hooks/exhaustive-deps */
}, [onClick, ...dependencies, useCapture]);

/**
 * Trigger click event when clicking outside of a given ref.
 * @private
 *
 * @see {@link https://github.com/gragland/usehooks/blob/master/src/pages/useOnClickOutside.md}
 *
 * @example
 * const ref = useRef();
 *
 * useOnClickOutside(ref, onClick);
 *
 * @param {React.RefObject | React.RefObject[]} ref Ref to manage outside click
 * @param {Function} onClick Click event handler
 * @param {Boolean} [useCapture=false] Whether to dispatch before any events beneath it in the DOM tree
 * @returns {undefined}
 */

const useOnClickOutside = (ref, onClick, useCapture = false) => {
  const listener = event => {
    const elementRef = Array.isArray(ref) ? ref : [ref];

    if (elementRef.some(ref2 => {
      var _a;

      return (_a = ref2.current) === null || _a === void 0 ? void 0 : _a.contains(event.target);
    })) {
      return;
    }

    onClick(event);
  };

  useOnClick(listener, [ref, listener], useCapture);
};

/**
 * Trigger key down event.
 * @private
 *
 * @param {string[]} keys Collection of `KeyboardEvent.key`s to filter
 * https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/key
 * @param {Function} onKeyDown Listener for filtered `keydown` events
 * @returns {undefined}
 */

const useOnKeyDown = (keys, onKeyDown) => React.useEffect(() => {
  const listener = event => {
    if (keys.includes(event.key) && typeof onKeyDown === "function") {
      onKeyDown(event);
    }
  };

  document.addEventListener("keydown", listener);
  return () => document.removeEventListener("keydown", listener);
}, [keys, onKeyDown]);

/**
 * @private
 */

const BottomSheetPortal = props => {
  const _a = applyCommonProps(props, BottomSheetPortal, true),
        {
    a11yLabel,
    actions,
    closeButtonProps,
    children,
    isOpen = false,
    onClose,
    onClosed,
    title
  } = _a,
        rest = __rest(_a, ["a11yLabel", "actions", "closeButtonProps", "children", "isOpen", "onClose", "onClosed", "title"]);

  const labelCount = (a11yLabel ? 1 : 0) + (title ? 1 : 0) === 1;
  invariant(labelCount, "`BottomSheet` accessibility violation. `BottomSheet` requires a `title` OR an `a11yLabel`.");

  const _b = closeButtonProps || {},
        {
    onClick: closeButtonOnClick
  } = _b,
        closeButtonRest = __rest(_b, ["onClick"]);

  const bottomSheetRef = React.useRef(null);
  const titleId = useSSRSafeId();
  const [setScrollableContentElement] = useLockBodyScroll();
  useOnClickOutside(bottomSheetRef, onClose);
  useOnKeyDown(["Esc", "Escape"], onClose);
  return jsx(CSSTransition, Object.assign({
    classNames: {
      enter: style$14.transitionEnter,
      enterActive: style$14.transitionEnterActive,
      exit: style$14.transitionExit,
      exitActive: style$14.transitionExitActive
    },
    in: isOpen,
    mountOnEnter: true,
    onExited: () => onClosed === null || onClosed === void 0 ? void 0 : onClosed(),
    timeout: 900
  }, {
    children: jsxs(Overlay, Object.assign({}, rest, {
      children: [jsx("div", Object.assign({
        className: style$14.container
      }, {
        children: jsxs("div", Object.assign({
          className: style$14.bottomSheet,
          ref: bottomSheetRef,
          role: "dialog"
        }, a11yLabel && {
          "aria-label": a11yLabel
        }, title && {
          "aria-labelledby": titleId
        }, {
          children: [typeof title === "function" ? title({
            titleId
          }) : jsxs("div", Object.assign({
            className: style$14.header
          }, {
            children: [jsx(OverlayCloseButton, Object.assign({
              "aria-label": "Close dialog",
              onClick: event => {
                closeButtonOnClick === null || closeButtonOnClick === void 0 ? void 0 : closeButtonOnClick(event);
                onClose(event);
              }
            }, closeButtonRest), void 0), title && jsx("h2", Object.assign({
              className: style$14.title,
              id: titleId
            }, {
              children: title
            }), void 0)]
          }), void 0), jsx("div", Object.assign({
            className: style$14.content,
            ref: setScrollableContentElement
          }, {
            children: jsx("div", Object.assign({
              className: style$14.contentInner
            }, {
              children: children
            }), void 0)
          }), void 0), actions && jsx("div", Object.assign({
            className: style$14.actions
          }, {
            children: actions
          }), void 0)]
        }), void 0)
      }), void 0), jsx(OverlayScrim, {
        className: style$14.scrim
      }, void 0)]
    }), void 0)
  }), void 0);
};

/**
 * Bottom sheets are surfaces containing supplementary content that are anchored to the bottom of the screen.
 *
 * @see {@link https://livingdesign.walmart.com/components/bottom-sheet/react/}
 */

const BottomSheet = props => {
  const {
    isOpen,
    onClosed
  } = props,
        rest = __rest(props, ["isOpen", "onClosed"]);

  const [isTransitioning, setIsTransitioning] = React.useState(isOpen);
  const previousIsOpen = React.useRef(isOpen);
  /**
   * This hook delays `isOpen` property changes such that the
   * `BottomSheetPortal` component can transition in and out.
   */

  React.useEffect(() => {
    if (previousIsOpen.current === isOpen) {
      return;
    }

    previousIsOpen.current = isOpen;
    const handle = setTimeout(() => {
      setIsTransitioning(true);
    }, 0);
    /* eslint-disable-next-line consistent-return */

    return () => clearTimeout(handle);
  }, [isOpen]);

  if (!isOpen && !isTransitioning) {
    return null;
  }

  return jsx(BottomSheetPortal, Object.assign({
    isOpen: isOpen && isTransitioning,
    onClosed: () => {
      setIsTransitioning(false);
      onClosed === null || onClosed === void 0 ? void 0 : onClosed();
    }
  }, rest), void 0);
};
BottomSheet.displayName = "BottomSheet";

var style$10 = {"breadcrumb":"Breadcrumb-module_breadcrumb__OLCnl","item":"Breadcrumb-module_item__UsIYQ","separator":"Breadcrumb-module_separator__NdiRd"};

/**
 * The breadcrumb is a secondary navigation pattern that helps a user understand the hierarchy among levels and navigate back through them. Breadcrumbs show users their current location relative to the information architecture and enable them to quickly move up to a parent level or previous step.
 *
 * @see {@link https://livingdesign.walmart.com/components/breadcrumb/react/}
 */

const Breadcrumb = props => {
  const _a = applyCommonProps(props, Breadcrumb, true),
        {
    children,
    className
  } = _a,
        rest = __rest(_a, ["children", "className"]);

  const breadcrumbItemsCount = React.Children.count(children);
  const hasBreadcrumbItems = breadcrumbItemsCount > 0;
  invariant(hasBreadcrumbItems, "Invalid prop `children` supplied to `Breadcrumb`.");

  if (!hasBreadcrumbItems) {
    return null;
  }

  return jsx("nav", Object.assign({
    "aria-label": "breadcrumb",
    className: className
  }, rest, {
    children: jsx("ol", Object.assign({
      className: style$10.breadcrumb
    }, {
      children: React.Children.map(children, (child, index) => jsxs("li", Object.assign({
        className: style$10.item
      }, {
        children: [child, index < breadcrumbItemsCount - 1 && jsx("span", Object.assign({
          "aria-hidden": true,
          className: style$10.separator
        }, {
          children: "/"
        }), void 0)]
      }), void 0))
    }), void 0)
  }), void 0);
};
Breadcrumb.displayName = "Breadcrumb";

var style$$ = {"breadcrumbItem":"BreadcrumbItem-module_breadcrumbItem__lQKA5","current":"BreadcrumbItem-module_current__lnAtz"};

var style$_ = {"link":"Link-module_link__3U7E8"};

/**
 * Links are navigational elements.
 *
 * @see {@link https://livingdesign.walmart.com/components/link/react/}
 */

const Link = React.forwardRef((props, ref) => {
  const _a = applyCommonProps(props, Link, true),
        {
    children,
    className,
    target
  } = _a,
        rest = __rest(_a, ["children", "className", "target"]);

  return jsx("a", Object.assign({
    className: classNames(style$_.link, className),
    ref: ref
  }, getTarget(target), rest, {
    children: children
  }), void 0);
});
Link.displayName = "Link";

/**
 * @see {@link https://livingdesign.walmart.com/components/breadcrumb/react/}
 */

const BreadcrumbItem = React.forwardRef((props, ref) => {
  const _a = applyCommonProps(props, BreadcrumbItem, true),
        {
    children,
    className,
    isCurrent = false
  } = _a,
        rest = __rest(_a, ["children", "className", "isCurrent"]);

  return jsx(Link, Object.assign({
    className: classNames(style$$.breadcrumbItem, isCurrent && style$$.current, className),
    itemProp: "item",
    ref: ref
  }, isCurrent && {
    "aria-current": "location"
  }, rest, {
    children: jsx("span", Object.assign({
      itemProp: "name"
    }, {
      children: children
    }), void 0)
  }), void 0);
});
BreadcrumbItem.displayName = "BreadcrumbItem";

var style$Z = {"button":"Button-module_button__3Eu7g","destructive":"Button-module_destructive__3sObt","primary":"Button-module_primary__3_IxL","secondary":"Button-module_secondary__mEPCP","tertiary":"Button-module_tertiary__1mCAU","isFullWidth":"Button-module_isFullWidth__3hYxT","small":"Button-module_small__2k3yk","medium":"Button-module_medium__2LQ3F","large":"Button-module_large__WesBZ","leading":"Button-module_leading__1iDC7","trailing":"Button-module_trailing__3CkaZ"};

const isAnchor$1 = props => "href" in props;
/**
 * A button allows users to take actions and make choices, with a single press or a tap. Buttons are used as calls-to-action (CTA) across the experience. They are organized in the order or their importance: primary, secondary, and tertiary.
 *
 * @see {@link https://livingdesign.walmart.com/components/button/react/}
 */


const Button = React.forwardRef((props, ref) => {
  const _a = applyCommonProps(props, Button, true),
        {
    className,
    children,
    isFullWidth = false,
    leading,
    size = "small",
    trailing,
    variant = "secondary"
  } = _a,
        rest = __rest(_a, ["className", "children", "isFullWidth", "leading", "size", "trailing", "variant"]);

  const classes = classNames(style$Z.button, variant === "destructive" && style$Z.destructive, variant === "primary" && style$Z.primary, variant === "secondary" && style$Z.secondary, variant === "tertiary" && style$Z.tertiary, isFullWidth && style$Z.isFullWidth, size === "large" && style$Z.large, size === "medium" && style$Z.medium, size === "small" && style$Z.small, className);

  const content = jsxs(Fragment, {
    children: [leading && jsx("span", Object.assign({
      className: style$Z.leading
    }, {
      children: leading
    }), void 0), children, trailing && jsx("span", Object.assign({
      className: style$Z.trailing
    }, {
      children: trailing
    }), void 0)]
  }, void 0);

  if (isAnchor$1(props)) {
    const _b = rest,
          {
      href,
      onClick,
      onKeyDown: anchorOnKeyDown,
      target
    } = _b,
          anchorProps = __rest(_b, ["href", "onClick", "onKeyDown", "target"]); // Looks like a button should act like a button.
    //
    // @see {@link https://jira.walmart.com/browse/LD-374?focusedCommentId=12802856&page=com.atlassian.jira.plugin.system.issuetabpanels%3Acomment-tabpanel#comment-12802856}


    const onKeyDown = onAnchorKeyDown({
      href,
      onClick,
      onKeyDown: anchorOnKeyDown
    });
    return jsx("a", Object.assign({
      className: classes,
      href: href,
      onClick: onClick,
      onKeyDown: onKeyDown,
      ref: ref
    }, getTarget(target), anchorProps, {
      children: content
    }), void 0);
  }

  const _c = rest,
        {
    type = "button"
  } = _c,
        buttonProps = __rest(_c, ["type"]);

  return jsx("button", Object.assign({
    className: classes,
    ref: ref,
    type: type
  }, buttonProps, {
    children: content
  }), void 0);
});
Button.displayName = "Button";

var style$Y = {"buttonGroup":"ButtonGroup-module_buttonGroup__2XHJr"};

/**
 * Button group displays multiple related actions in a horizontal row to help with arrangement and spacing.
 *
 * @see {@link https://livingdesign.walmart.com/components/button-group/react/}
 */

const ButtonGroup = props => {
  const _a = applyCommonProps(props, ButtonGroup, true),
        {
    children,
    className
  } = _a,
        rest = __rest(_a, ["children", "className"]);

  return jsx("div", Object.assign({
    className: classNames(style$Y.buttonGroup, className)
  }, rest, {
    children: children
  }), void 0);
};
ButtonGroup.displayName = "ButtonGroup";

var style$X = {"callout":"Callout-module_callout__ZJUbM","enter":"Callout-module_enter__gi_cr","bottomCenter":"Callout-module_bottomCenter__1ywC1","bottomLeft":"Callout-module_bottomLeft__18Pab","bottomRight":"Callout-module_bottomRight__4wf0j","left":"Callout-module_left__xMVET","right":"Callout-module_right__uX4rQ","topCenter":"Callout-module_topCenter__2x7eA","topLeft":"Callout-module_topLeft__2uc9J","topRight":"Callout-module_topRight__3tPoW","enterActive":"Callout-module_enterActive__13-2S","exit":"Callout-module_exit__AGS6d","exitActive":"Callout-module_exitActive__s0RUl"};

var style$W = {"baseCallout":"BaseCallout-module_baseCallout__2dtCI","bottomCenter":"BaseCallout-module_bottomCenter__2Bf8W","bottomLeft":"BaseCallout-module_bottomLeft__3oczZ","bottomRight":"BaseCallout-module_bottomRight__371oA","left":"BaseCallout-module_left__6bEEV","right":"BaseCallout-module_right__1sfjC","topCenter":"BaseCallout-module_topCenter__3knt2","topLeft":"BaseCallout-module_topLeft__2D_Mp","topRight":"BaseCallout-module_topRight__3eIpk","content":"BaseCallout-module_content__3T8QC","label":"BaseCallout-module_label__3sU1U","actions":"BaseCallout-module_actions__1puyZ"};

var style$V = {"closeButton":"CalloutCloseButton-module_closeButton__34jK8"};

/**
 * @private
 */

const CalloutCloseButton = React.forwardRef((props, ref) => {
  const {
    className
  } = props,
        rest = __rest(props, ["className"]);

  return jsx("button", Object.assign({
    className: classNames(style$V.closeButton, className),
    ref: ref,
    type: "button"
  }, rest), void 0);
});

/**
 * @private
 */

const BaseCallout = React.forwardRef((props, ref) => {
  const {
    children,
    className,
    closeButtonProps,
    onClose,
    position
  } = props,
        rest = __rest(props, ["children", "className", "closeButtonProps", "onClose", "position"]);

  return jsx("div", Object.assign({
    className: classNames(style$W.baseCallout, position === "bottomCenter" && style$W.bottomCenter, position === "bottomLeft" && style$W.bottomLeft, position === "bottomRight" && style$W.bottomRight, position === "left" && style$W.left, position === "right" && style$W.right, position === "topCenter" && style$W.topCenter, position === "topLeft" && style$W.topLeft, position === "topRight" && style$W.topRight, className),
    ref: ref
  }, rest, {
    children: jsxs("div", Object.assign({
      className: style$W.content
    }, {
      children: [jsx("div", Object.assign({
        className: style$W.label
      }, {
        children: children
      }), void 0), jsx("div", Object.assign({
        className: style$W.actions
      }, {
        children: jsx(CalloutCloseButton, Object.assign({
          children: "Close"
        }, closeButtonProps, {
          onClick: event => {
            var _a;

            (_a = closeButtonProps === null || closeButtonProps === void 0 ? void 0 : closeButtonProps.onClick) === null || _a === void 0 ? void 0 : _a.call(closeButtonProps, event);
            onClose(event);
          }
        }), void 0)
      }), void 0)]
    }), void 0)
  }), void 0);
});

var style$U = {"layoutManager":"CalloutLayoutManager-module_layoutManager__23c8V","bottom":"CalloutLayoutManager-module_bottom__3-9AN","left":"CalloutLayoutManager-module_left__2dWcZ","right":"CalloutLayoutManager-module_right__2UN3-","top":"CalloutLayoutManager-module_top__2MeVo"};

/**
 * Get position styles for a referrer element and a target element.
 * @private
 *
 * @param {Object} options
 * @param {Function} options.calculatePosition Get the left and top positions
 * @param {React.RefObject<HTMLElement>} options.referrerRef The element that refers to the target
 * @param {React.RefObject<HTMLElement>} options.targetRef The target element
 * @returns {Object} The alignment styles
 */
const getPositionStyle = ({
  calculatePosition,
  referrerRef,
  targetRef
}) => {
  var _a, _b;

  const referrerDOMRect = ((_a = referrerRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect()) || {
    height: 0,
    width: 0
  };
  const targetDOMRect = ((_b = targetRef.current) === null || _b === void 0 ? void 0 : _b.getBoundingClientRect()) || {
    height: 0,
    width: 0
  };
  const {
    left,
    top
  } = calculatePosition({
    referrerHeight: referrerDOMRect.height,
    referrerWidth: referrerDOMRect.width,
    targetHeight: targetDOMRect.height,
    targetWidth: targetDOMRect.width
  });
  return {
    // NOTE: Leverage GPU for positioning
    // @see {@link https://www.paulirish.com/2012/why-moving-elements-with-translate-is-better-than-posabs-topleft/}
    // @see {@link https://www.oreilly.com/content/animating-movement-with-translate3d/}
    transform: `translate3d(${left}px, ${top}px, 0)`
  };
};

const getCalloutPositionStyle = ({
  position,
  referrerRef,
  targetRef
}) => getPositionStyle({
  calculatePosition({
    referrerHeight,
    referrerWidth,
    targetHeight,
    targetWidth
  }) {
    let left;

    if (position === "left") {
      left = -1 * referrerWidth;
    } else if (position === "right") {
      left = targetWidth;
    } else if (position === "bottomLeft" || position === "topLeft") {
      left = targetWidth / 2 - referrerWidth + 24;
    } else if (position === "bottomRight" || position === "topRight") {
      left = targetWidth / 2 - 24;
    } else {
      left = (targetWidth - referrerWidth) / 2;
    }

    let top;

    if (position.startsWith("bottom")) {
      top = targetHeight;
    } else if (position.startsWith("top")) {
      top = -1 * referrerHeight;
    } else {
      top = (targetHeight - referrerHeight) / 2;
    }

    return {
      left,
      top
    };
  },

  referrerRef,
  targetRef
});

/**
 * @private
 */

const CalloutLayoutManager = props => {
  const {
    className,
    position,
    targetRef
  } = props,
        rest = __rest(props, ["className", "position", "targetRef"]);

  const [rootStyle, setRootStyle] = React.useState({
    // NOTE: hide the callout until it has been mounted and positioned
    visibility: "hidden"
  });
  const referrerRef = React.useRef(null);
  React.useEffect(() => {
    setRootStyle(getCalloutPositionStyle({
      position,
      referrerRef,
      targetRef
    }));
  }, [position, referrerRef, targetRef]);
  return jsx("div", Object.assign({
    className: classNames(style$U.layoutManager, position === "bottomCenter" && style$U.bottom, position === "bottomLeft" && style$U.bottom, position === "bottomRight" && style$U.bottom, position === "left" && style$U.left, position === "right" && style$U.right, position === "topCenter" && style$U.top, position === "topLeft" && style$U.top, position === "topRight" && style$U.top, className),
    ref: referrerRef,
    style: rootStyle
  }, rest), void 0);
};

/**
 * Callouts are overlays that display onboarding or coaching content.
 *
 * @see {@link https://livingdesign.walmart.com/components/callout/react/}
 */

const Callout = props => {
  const _a = applyCommonProps(props, Callout, true),
        {
    a11yContentLabel,
    closeButtonProps,
    children,
    className,
    content,
    isOpen = false,
    onClose,
    position = "bottomCenter",
    targetRef
  } = _a,
        rest = __rest(_a, ["a11yContentLabel", "closeButtonProps", "children", "className", "content", "isOpen", "onClose", "position", "targetRef"]);

  const child = React.Children.only(children);
  invariant(!!a11yContentLabel.trim(), "`Callout` accessibility violation. `Callout` requires an `a11yContentLabel`.");
  const buttonId = useSSRSafeId(child.props.id);
  const contentId = useSSRSafeId();
  const previousIsOpen = React.useRef(isOpen);
  const ref = React.useRef(null);
  React.useEffect(() => {
    var _a;

    if (!isOpen && previousIsOpen.current) {
      (_a = targetRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    }

    previousIsOpen.current = isOpen;
  }, [isOpen, previousIsOpen, targetRef]);
  return jsxs("span", Object.assign({
    className: classNames(style$X.callout, position === "bottomCenter" && style$X.bottomCenter, position === "bottomLeft" && style$X.bottomLeft, position === "bottomRight" && style$X.bottomRight, position === "left" && style$X.left, position === "right" && style$X.right, position === "topCenter" && style$X.topCenter, position === "topLeft" && style$X.topLeft, position === "topRight" && style$X.topRight, className)
  }, rest, {
    children: [React.cloneElement(child, {
      "aria-labelledby": isOpen ? `${buttonId} ${contentId}` : undefined,

      /**
       * @note Emitting `id` when Callout is server-side rendered causes
       * problems with client-side hydration when `id`s to not match. Passing
       * `child.props.id` when the callout is closed gets around the issue.
       *
       * @see {@link https://reactjs.org/docs/react-dom.html#hydrate}
       * @see {@link https://jira.walmart.com/browse/LD-2066}
       */
      id: isOpen ? buttonId : child.props.id
    }), jsx(CSSTransition, Object.assign({
      classNames: {
        enter: style$X.enter,
        enterActive: style$X.enterActive,
        exit: style$X.exit,
        exitActive: style$X.exitActive
      },
      in: isOpen,
      mountOnEnter: true,
      nodeRef: ref,
      timeout: 200,
      unmountOnExit: true
    }, {
      children: jsx(CalloutLayoutManager, Object.assign({
        position: position,
        targetRef: targetRef
      }, {
        children: jsxs(BaseCallout, Object.assign({
          closeButtonProps: Object.assign({
            "aria-label": `Close ${a11yContentLabel}`
          }, closeButtonProps),
          onClose: onClose,
          ref: ref,
          position: position
        }, {
          children: [jsx(VisuallyHidden, {
            children: a11yContentLabel
          }, void 0), jsx("span", Object.assign({
            id: contentId
          }, {
            children: content
          }), void 0)]
        }), void 0)
      }), void 0)
    }), void 0)]
  }), void 0);
};
Callout.displayName = "Callout";

var style$T = {"card":"Card-module_card__1r4qX","small":"Card-module_small__1EnsG","cardItem":"Card-module_cardItem__2D4Q_","medium":"Card-module_medium__2g_sH","large":"Card-module_large__2ZfCs"};

/**
 * Cards organize and present similiar content that users can scan quickly and interact with. They always show relevant content and actionable information. They contain text, images, icons and buttons placed with hierarchy, and may be placed as a series or feed of similar content.
 *
 * @see {@link https://livingdesign.walmart.com/components/card/react/}
 */

const Card = props => {
  const _a = applyCommonProps(props, Card, true),
        {
    children,
    className,
    size = "medium"
  } = _a,
        rest = __rest(_a, ["children", "className", "size"]);

  return jsx("div", Object.assign({
    className: classNames(style$T.card, size === "medium" && style$T.medium, size === "large" && style$T.large, size === "small" && style$T.small, className)
  }, rest, {
    children: children
  }), void 0);
};
Card.displayName = "Card";

/**
 * @see {@link https://livingdesign.walmart.com/components/card/react/}
 */

const CardItem = props => {
  const _a = applyCommonProps(props, CardItem, true),
        {
    children,
    className
  } = _a,
        rest = __rest(_a, ["children", "className"]);

  return jsx("div", Object.assign({
    className: classNames(style$T.cardItem, className)
  }, rest, {
    children: children
  }), void 0);
};
CardItem.displayName = "CardItem";

var style$S = {"label":"Checkbox-module_label__3dPQL","checked":"Checkbox-module_checked__Y0-5W","disabled":"Checkbox-module_disabled__3cipw","input":"Checkbox-module_input__NFiDk","checkbox":"Checkbox-module_checkbox__SVgXV","labelText":"Checkbox-module_labelText__14PgT"};

/**
 * Checkboxes are used for a list of options where the user may select multiple options, including all or none.
 *
 * @see {@link https://livingdesign.walmart.com/components/checkbox/react/}
 */

const Checkbox = React.forwardRef((props, ref) => {
  const _a = applyCommonProps(props, Checkbox, true),
        {
    a11yLabelledBy,
    checkboxProps = {},
    checked = false,
    className,
    disabled = false,
    id: initialId,
    label,
    name,
    onChange,
    value
  } = _a,
        rest = __rest(_a, ["a11yLabelledBy", "checkboxProps", "checked", "className", "disabled", "id", "label", "name", "onChange", "value"]);

  const labelCount = (label ? 1 : 0) + (a11yLabelledBy ? 1 : 0) === 1;
  invariant(labelCount, "`Checkbox` accessibility violation. `Checkbox` requires a `label` OR an `a11yLabelledBy`.");

  const {
    className: checkboxClassName
  } = checkboxProps,
        checkboxRest = __rest(checkboxProps, ["className"]);

  const id = useSSRSafeId(initialId);
  return jsxs("label", Object.assign({
    className: classNames(style$S.label, checked && style$S.checked, disabled && style$S.disabled, className),
    htmlFor: id
  }, rest, {
    children: [jsx("input", Object.assign({
      checked: checked,
      className: classNames(style$S.input, checkboxClassName),
      disabled: disabled,
      id: id,
      name: name,
      onChange: onChange,
      ref: ref,
      type: "checkbox"
    }, a11yLabelledBy && {
      "aria-labelledby": a11yLabelledBy
    }, value && {
      value
    }, checkboxRest), void 0), jsx("i", {
      className: style$S.checkbox
    }, void 0), label && jsx("span", Object.assign({
      className: style$S.labelText
    }, {
      children: label
    }), void 0)]
  }), void 0);
});
Checkbox.displayName = "Checkbox";

var style$R = {"chip":"Chip-module_chip__2YGAJ","selected":"Chip-module_selected__3CEKA","small":"Chip-module_small__2OE4J","large":"Chip-module_large__1FVYD","leading":"Chip-module_leading__1ygkC","trailing":"Chip-module_trailing__2Y6K5"};

/**
 * Chips allow users to make selections, filter content, or trigger actions.
 *
 * @see {@link https://livingdesign.walmart.com/components/chip/react/}
 */

const Chip = React.forwardRef((props, ref) => {
  const _a = applyCommonProps(props, Chip, true),
        {
    children,
    className,
    leading,
    trailing,
    selected = false,
    size = "small"
  } = _a,
        rest = __rest(_a, ["children", "className", "leading", "trailing", "selected", "size"]);

  return jsxs("button", Object.assign({
    "aria-pressed": selected,
    className: classNames(style$R.chip, size === "large" && style$R.large, size === "small" && style$R.small, selected && style$R.selected, className),
    ref: ref,
    type: "button"
  }, rest, {
    children: [leading && jsx("span", Object.assign({
      className: style$R.leading
    }, {
      children: leading
    }), void 0), children, trailing && jsx("span", Object.assign({
      className: style$R.trailing
    }, {
      children: trailing
    }), void 0)]
  }), void 0);
});
Chip.displayName = "Chip";

var style$Q = {"chipGroup":"ChipGroup-module_chipGroup__3YiMm"};

/**
 * Chip group displays multiple related chips in a horizontal row to help with arrangement and spacing.
 *
 * @see {@link https://livingdesign.walmart.com/components/chip-group/react/}
 */

const ChipGroup = props => {
  const _a = applyCommonProps(props, ChipGroup, true),
        {
    children,
    className
  } = _a,
        rest = __rest(_a, ["children", "className"]);

  return jsx("div", Object.assign({
    className: classNames(style$Q.chipGroup, className)
  }, rest, {
    children: children
  }), void 0);
};
ChipGroup.displayName = "ChipGroup";

var style$P = {"collapse":"Collapse-module_collapse__3Rtuq","closed":"Collapse-module_closed__2a_GU"};

/**
 * Height transitions for the `Collapse` component.
 * @private
 *
 * @note This hook is separate from the `Collapse` component to permit mocking
 * such that `setTimeout` doesn't fire during testing.
 *
 * @param {boolean} isOpen Whether the collapse is open
 * @param {React.RefObject} ref The collapse's element's React ref
 * @returns {Object}
 */

const useCollapseTransition = (isOpen, ref) => {
  const [height, setHeight] = React.useState(isOpen ? "auto" : 0);
  const [isTransitioning, setIsTransitioning] = React.useState(false);
  const previousIsOpen = React.useRef(isOpen);
  /**
   * Observe changes to `isOpen` and initiate the appropriate state changes.
   *
   * @note `React.useLayoutEffect` is necessary for transitions to work.
   */

  React.useLayoutEffect(() => {
    const {
      current: collapse
    } = ref;

    if (isOpen === previousIsOpen.current || !collapse) {
      return;
    }

    previousIsOpen.current = isOpen;
    setHeight(collapse.scrollHeight);
    setIsTransitioning(true);

    if (!isOpen) {
      const timeoutId = setTimeout(() => setHeight(0), 0);
      /* eslint-disable-next-line consistent-return */

      return () => clearTimeout(timeoutId);
    }
  }, [isOpen, ref]);
  return {
    height,
    isTransitioning,

    onTransitionEnd() {
      setIsTransitioning(false);
      /**
       * Set `height: auto` when the Collapse is open so it resizes when its
       * childrens' layout changes.
       */

      if (isOpen) {
        setHeight("auto");
      }
    }

  };
};

/**
 * Trigger click event when clicking inside of a given ref.
 * @private
 *
 * @see {@link https://github.com/gragland/usehooks/blob/master/src/pages/useOnClickOutside.md}
 *
 * @example
 * const ref1 = useRef();
 * const ref2 = useRef();
 *
 * useOnClickInside(ref1, onClick);
 * useOnClickInside([ref1, ref2], onClick);
 *
 * @param {React.RefObject | React.RefObject[]} ref Ref to manage inside click
 * @param {Function} onClick Click event handler
 * @param {Boolean} [useCapture=false] Whether to dispatch before any events beneath it in the DOM tree
 * @returns {undefined}
 */

const useOnClickInside = (ref, onClick, useCapture = false) => {
  const listener = event => {
    const elementRef = Array.isArray(ref) ? ref : [ref];

    if (elementRef.some(ref2 => {
      var _a;

      return (_a = ref2.current) === null || _a === void 0 ? void 0 : _a.contains(event.target);
    }) === false) {
      return;
    }

    onClick(event);
  };

  useOnClick(listener, [ref, listener], useCapture);
};

/**
 * A utility component that is used to put long sections of information under a block that users can expand or collapse.
 *
 * @see {@link https://livingdesign.walmart.com/utilities/collapse/react/}
 */

const Collapse = props => {
  const _a = applyCommonProps(props, Collapse, true),
        {
    className,
    isOpen = false,
    onTransitionEnd: initialOnTransitionEnd,
    style: initialStyle
  } = _a,
        rest = __rest(_a, ["className", "isOpen", "onTransitionEnd", "style"]);

  const ref = React.useRef(null);
  const {
    height,
    isTransitioning,
    onTransitionEnd
  } = useCollapseTransition(isOpen, ref);
  return jsx("div", Object.assign({
    className: classNames(style$P.collapse, !isOpen && style$P.closed, className),
    onTransitionEnd: event => {
      onTransitionEnd();
      initialOnTransitionEnd === null || initialOnTransitionEnd === void 0 ? void 0 : initialOnTransitionEnd(event);
    },
    ref: ref,
    style: Object.assign(Object.assign({
      height
    }, !isTransitioning && height === 0 && {
      visibility: "hidden"
    }), initialStyle)
  }, rest), void 0);
};
Collapse.displayName = "Collapse";

var style$O = {"container":"Container-module_container__2a5_Q"};

/**
 * A utility component that sets a maximum width for layouts.
 *
 * @see {@link https://livingdesign.walmart.com/utilities/container/react/}
 */

const Container = props => {
  const _a = applyCommonProps(props, Container, true),
        {
    children,
    className
  } = _a,
        rest = __rest(_a, ["children", "className"]);

  return jsx("div", Object.assign({
    className: classNames(style$O.container, className)
  }, rest, {
    children: children
  }), void 0);
};
Container.displayName = "Container";

var style$N = {"dataTable":"DataTable-module_dataTable__1a5Sj"};

const DataTable = props => {
  const _a = applyCommonProps(props, DataTable),
        {
    className
  } = _a,
        rest = __rest(_a, ["className"]);

  return jsx("table", Object.assign({
    className: classNames(style$N.dataTable, className)
  }, rest), void 0);
};

const DataTableBody = props => {
  const rest = applyCommonProps(props, DataTableBody);
  return jsx("tbody", Object.assign({}, rest), void 0);
};

var style$M = {"dataTableCellAlphaNumeric":"DataTableCellAlphaNumeric-module_dataTableCellAlphaNumeric__2oWK1"};

var style$L = {"body":"Body-module_body__FhbCV","body1":"Body-module_body1__dqtk7","body2":"Body-module_body2__2aVnm"};

/**
 * @see {@link https://livingdesign.walmart.com/components/text/body/react/}
 */

const Body = props => {
  const _a = applyCommonProps(props, Body, true),
        {
    as: Component = "span",
    className,
    variant = "1"
  } = _a,
        rest = __rest(_a, ["as", "className", "variant"]);

  return jsx(Component, Object.assign({
    className: classNames(style$L.body, variant === "1" && style$L.body1, variant === "2" && style$L.body2, className)
  }, rest), void 0);
};
Body.displayName = "Body";

var style$K = {"caption":"Caption-module_caption__yOOiI"};

/**
 * @see {@link https://livingdesign.walmart.com/components/text/caption/react/}
 */

const Caption = props => {
  const _a = applyCommonProps(props, Caption, true),
        {
    as: Component = "span",
    className
  } = _a,
        rest = __rest(_a, ["as", "className"]);

  return jsx(Component, Object.assign({
    className: classNames(style$K.caption, className)
  }, rest), void 0);
};
Caption.displayName = "Caption";

var style$J = {"display":"Display-module_display__2qFT-","display1":"Display-module_display1__g6vQd","display2":"Display-module_display2__1O5lV"};

/**
 * @see {@link https://livingdesign.walmart.com/components/text/display/react/}
 */

const Display = props => {
  const _a = applyCommonProps(props, Display, true),
        {
    as: Component = "span",
    className,
    variant = "1"
  } = _a,
        rest = __rest(_a, ["as", "className", "variant"]);

  return jsx(Component, Object.assign({
    className: classNames(style$J.display, variant === "1" && style$J.display1, variant === "2" && style$J.display2, className)
  }, rest), void 0);
};
Display.displayName = "Display";

var style$I = {"heading":"Heading-module_heading__2wRYs"};

/**
 * @see {@link https://livingdesign.walmart.com/components/text/heading/react/}
 */

const Heading = props => {
  const _a = applyCommonProps(props, Heading, true),
        {
    as: Component = "span",
    className
  } = _a,
        rest = __rest(_a, ["as", "className"]);

  return jsx(Component, Object.assign({
    className: classNames(style$I.heading, className)
  }, rest), void 0);
};
Heading.displayName = "Heading";

var style$H = {"pageTitle":"PageTitle-module_pageTitle__15Chj"};

/**
 * @see {@link https://livingdesign.walmart.com/components/text/page-title/react/}
 */

const PageTitle = props => {
  const _a = applyCommonProps(props, PageTitle, true),
        {
    as: Component = "span",
    className
  } = _a,
        rest = __rest(_a, ["as", "className"]);

  return jsx(Component, Object.assign({
    className: classNames(style$H.pageTitle, className)
  }, rest), void 0);
};
PageTitle.displayName = "PageTitle";

var style$G = {"subheading":"Subheading-module_subheading__3uLu4","subheading1":"Subheading-module_subheading1__X2vTy","subheading2":"Subheading-module_subheading2__3zpom"};

/**
 * @see {@link https://livingdesign.walmart.com/components/text/subheading/react/}
 */

const Subheading = props => {
  const _a = applyCommonProps(props, Subheading, true),
        {
    as: Component = "span",
    className,
    variant = "1"
  } = _a,
        rest = __rest(_a, ["as", "className", "variant"]);

  return jsx(Component, Object.assign({
    className: classNames(style$G.subheading, variant === "1" && style$G.subheading1, variant === "2" && style$G.subheading2, className)
  }, rest), void 0);
};
Subheading.displayName = "Subheading";

const DataTableCellAlphaNumeric = props => {
  const _a = applyCommonProps(props, DataTableCellAlphaNumeric),
        {
    children,
    className
  } = _a,
        rest = __rest(_a, ["children", "className"]);

  return jsx("td", Object.assign({
    className: classNames(style$M.dataTableCellAlphaNumeric, className)
  }, rest, {
    children: jsx(Body, {
      children: children
    }, void 0)
  }), void 0);
};

var style$F = {"dataTableCellNumeric":"DataTableCellNumeric-module_dataTableCellNumeric__r0Azr"};

const DataTableCellNumeric = props => {
  const _a = applyCommonProps(props, DataTableCellNumeric),
        {
    className
  } = _a,
        rest = __rest(_a, ["className"]);

  return jsx("td", Object.assign({
    className: classNames(style$F.dataTableCellNumeric, className)
  }, rest), void 0);
};

const DataTableHead = props => {
  const rest = applyCommonProps(props, DataTableHead);
  return jsx("thead", Object.assign({}, rest), void 0);
};

var style$E = {"tableHeader":"DataTableHeader-module_tableHeader__26yJF","dataTableHeader":"DataTableHeader-module_dataTableHeader__1_Yre","left":"DataTableHeader-module_left__2RQNQ","right":"DataTableHeader-module_right__ASAK6","button":"DataTableHeader-module_button__1DYm7","icon":"DataTableHeader-module_icon__2QSEo","sorted":"DataTableHeader-module_sorted__3Lltl"};

const DataTableHeader = props => {
  const _a = applyCommonProps(props, DataTableHeader),
        {
    alignment = "left",
    children,
    className,
    onSort,
    sort = "none"
  } = _a,
        rest = __rest(_a, ["alignment", "children", "className", "onSort", "sort"]);

  const {
    announcePolite
  } = useA11yAnnouncement();
  const previousSort = React.useRef(sort);
  let icon;

  if (sort === "descending") {
    icon = jsx(ArrowDown, {
      className: style$E.icon
    }, void 0);
  } else if (sort === "ascending" || onSort) {
    icon = jsx(ArrowUp, {
      className: style$E.icon
    }, void 0);
  }

  React.useEffect(() => {
    if (previousSort.current !== sort) {
      previousSort.current = sort;
      announcePolite(`Sorted ${children} by ${sort}`);
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */

  }, [children, sort]);
  return jsx("th", Object.assign({
    className: classNames(style$E.tableHeader, alignment === "left" && style$E.left, alignment === "right" && style$E.right, className),
    scope: "col"
  }, onSort && {
    "aria-sort": sort
  }, rest, {
    children: onSort ? jsxs("button", Object.assign({
      className: classNames(style$E.dataTableHeader, style$E.button, (sort === "ascending" || sort === "descending") && style$E.sorted),
      onClick: onSort,
      type: "button"
    }, {
      children: [alignment === "right" && icon, jsx(Subheading, Object.assign({
        variant: "1"
      }, {
        children: children
      }), void 0), alignment === "left" && icon]
    }), void 0) : jsx("span", Object.assign({
      className: style$E.dataTableHeader
    }, {
      children: jsx(Subheading, Object.assign({
        variant: "1"
      }, {
        children: children
      }), void 0)
    }), void 0)
  }), void 0);
};

var styles$1 = {"dataTableRow":"DataTableRow-module_dataTableRow__31PiA"};

const DataTableRow = props => {
  const _a = applyCommonProps(props, DataTableRow),
        {
    className
  } = _a,
        rest = __rest(_a, ["className"]);

  return jsx("tr", Object.assign({
    className: classNames(styles$1.dataTableRow, className)
  }, rest), void 0);
};

var style$D = {"divider":"Divider-module_divider__DXnQt"};

/**
 * Dividers create visual breaks in content.
 *
 * @see {@link https://livingdesign.walmart.com/components/divider/react/}
 */

const Divider = props => {
  const _a = applyCommonProps(props, Divider, true),
        {
    "aria-label": ariaLabel,
    className,
    title
  } = _a,
        rest = __rest(_a, ["aria-label", "className", "title"]); // Convert boolean strings to pure booleans. !bool can have unintended side-effects.
  // `ariaHidden` should default to `true` unless overridden.


  const ariaHidden = !(rest["aria-hidden"] === "false" || rest["aria-hidden"] === false);
  invariant(!!(ariaHidden || ariaLabel !== undefined || title !== undefined), "`Divider` accessibility violation. `Divider` requires an accessible label if `aria-hidden` is false.");
  return jsx("hr", Object.assign({
    "aria-hidden": ariaHidden,
    className: classNames(style$D.divider, className)
  }, title && {
    title
  }, rest), void 0);
};
Divider.displayName = "Divider";

var style$C = {"formGroup":"FormGroup-module_formGroup__2ToDm","formGroupLegend":"FormGroup-module_formGroupLegend__Pr1ON","formGroupRow":"FormGroup-module_formGroupRow__20CGQ","helperText":"FormGroup-module_helperText__2Zgz6","label":"FormGroup-module_label__3B88h"};

var style$B = {"error":"FormError-module_error__1jmFb","icon":"FormError-module_icon__35XTM"};

/**
 * @private
 */

const FormError = props => {
  const {
    children,
    className
  } = props,
        rest = __rest(props, ["children", "className"]);

  return jsxs("span", Object.assign({
    className: classNames(style$B.error, className)
  }, rest, {
    children: [children, jsx(ExclamationCircleFill, {
      "aria-hidden": true,
      className: style$B.icon
    }, void 0)]
  }), void 0);
};

var style$A = {"helperText":"FormHelperText-module_helperText__2gG_L"};

/**
 * @private
 */

const FormHelperText = props => {
  const {
    className
  } = props,
        rest = __rest(props, ["className"]);

  return jsx("span", Object.assign({
    className: classNames(style$A.helperText, className)
  }, rest), void 0);
};

var style$z = {"label":"FormLabel-module_label__3_IAI","text":"FormLabel-module_text__1M3HI","isEmpty":"FormLabel-module_isEmpty__2tWen","isFocused":"FormLabel-module_isFocused__LRYTB","isDisabled":"FormLabel-module_isDisabled__1d4BA"};

/**
 * @private
 */

const FormLabel = props => {
  const {
    children,
    className,
    disabled = false,
    isEmpty = false,
    isFocused = false
  } = props,
        rest = __rest(props, ["children", "className", "disabled", "isEmpty", "isFocused"]);

  return jsx("label", Object.assign({
    className: classNames(style$z.label, disabled && style$z.isDisabled, isEmpty && style$z.isEmpty, isFocused && style$z.isFocused, className)
  }, rest, {
    children: jsx("span", Object.assign({
      className: style$z.text
    }, {
      children: children
    }), void 0)
  }), void 0);
};

/**
 * A Form Group lets you create a list of grouped form elements. The form elements can be radio buttons, checkboxes, text area, etc.
 *
 * @see {@link https://livingdesign.walmart.com/components/form-group/react/}
 */

const FormGroup = props => {
  const _a = applyCommonProps(props, FormGroup),
        {
    children,
    className,
    error,
    helperText,
    label
  } = _a,
        rest = __rest(_a, ["children", "className", "error", "helperText", "label"]);

  return jsxs("fieldset", Object.assign({
    className: classNames(style$C.formGroup, className)
  }, rest, {
    children: [(error || helperText || label) && jsxs("legend", Object.assign({
      className: style$C.formGroupLegend
    }, {
      children: [label && jsx("span", Object.assign({
        className: style$C.label
      }, {
        children: label
      }), void 0), (error || helperText) && jsxs(FormHelperText, Object.assign({
        className: style$C.helperText
      }, {
        children: [error && jsx(FormError, {
          children: error
        }, void 0), !error && jsx("span", {
          children: helperText
        }, void 0)]
      }), void 0)]
    }), void 0), React.Children.map(children, child => jsx("div", Object.assign({
      className: style$C.formGroupRow
    }, {
      children: child
    }), void 0))]
  }), void 0);
};
FormGroup.displayName = "FormGroup";

var style$y = {"grid":"Grid-module_grid__3DN8v","gutter":"Grid-module_gutter__2xZlT"};

/**
 * The grid provides the framework for the rhythmic and consistent positioning of elements onscreen.
 *
 * @see {@link https://livingdesign.walmart.com/components/grid/react/}
 */

const Grid = props => {
  const _a = applyCommonProps(props, Grid, true),
        {
    children,
    className,
    hasGutter = false
  } = _a,
        rest = __rest(_a, ["children", "className", "hasGutter"]);

  return jsx("div", Object.assign({
    className: classNames(style$y.grid, hasGutter && style$y.gutter, className)
  }, rest, {
    children: React.Children.map(children, child => React.cloneElement(child, {
      hasGutter
    }))
  }), void 0);
};
Grid.displayName = "Grid";

var style$x = {"gridColumn":"GridColumn-module_gridColumn__2lqsw","small1":"GridColumn-module_small1__20LVl","small2":"GridColumn-module_small2__3NX3b","small3":"GridColumn-module_small3__3eyEp","small4":"GridColumn-module_small4__286DA","small5":"GridColumn-module_small5__3_F4H","small6":"GridColumn-module_small6__35bNd","small7":"GridColumn-module_small7__spAnc","small8":"GridColumn-module_small8__2yVt6","small9":"GridColumn-module_small9__17JwC","small10":"GridColumn-module_small10__1775P","small11":"GridColumn-module_small11__2Ugib","small12":"GridColumn-module_small12__2mzOm","medium1":"GridColumn-module_medium1__9582w","medium2":"GridColumn-module_medium2__1gPYx","medium3":"GridColumn-module_medium3__2YFMS","medium4":"GridColumn-module_medium4__BrYz6","medium5":"GridColumn-module_medium5__2S5vN","medium6":"GridColumn-module_medium6__O65Ns","medium7":"GridColumn-module_medium7__1F7sY","medium8":"GridColumn-module_medium8__4K5ft","medium9":"GridColumn-module_medium9__1HI7B","medium10":"GridColumn-module_medium10__lijUp","medium11":"GridColumn-module_medium11__2M1cr","medium12":"GridColumn-module_medium12__vtlD-","large1":"GridColumn-module_large1__Xc6Qn","large2":"GridColumn-module_large2__1w1Mq","large3":"GridColumn-module_large3__21uGZ","large4":"GridColumn-module_large4__RiUjq","large5":"GridColumn-module_large5__DGG8m","large6":"GridColumn-module_large6__1gIt9","large7":"GridColumn-module_large7__3KlnD","large8":"GridColumn-module_large8__QGce5","large9":"GridColumn-module_large9__Z7ZgI","large10":"GridColumn-module_large10__3p2rH","large11":"GridColumn-module_large11__3SndZ","large12":"GridColumn-module_large12__2s4ck","xlarge1":"GridColumn-module_xlarge1__2p6gF","xlarge2":"GridColumn-module_xlarge2__3SYRk","xlarge3":"GridColumn-module_xlarge3__3JV-O","xlarge4":"GridColumn-module_xlarge4__2kkJs","xlarge5":"GridColumn-module_xlarge5__1cTrA","xlarge6":"GridColumn-module_xlarge6__MrLOk","xlarge7":"GridColumn-module_xlarge7__1BS1u","xlarge8":"GridColumn-module_xlarge8__3Bpo4","xlarge9":"GridColumn-module_xlarge9__27mEo","xlarge10":"GridColumn-module_xlarge10__hs6bL","xlarge11":"GridColumn-module_xlarge11__3_j1Y","xlarge12":"GridColumn-module_xlarge12__2pRZY","xxlarge1":"GridColumn-module_xxlarge1__3S6TG","xxlarge2":"GridColumn-module_xxlarge2__2v4-E","xxlarge3":"GridColumn-module_xxlarge3__2FxX_","xxlarge4":"GridColumn-module_xxlarge4__2NreR","xxlarge5":"GridColumn-module_xxlarge5__1XmeG","xxlarge6":"GridColumn-module_xxlarge6__2r0Ql","xxlarge7":"GridColumn-module_xxlarge7__1gwFC","xxlarge8":"GridColumn-module_xxlarge8__3fO26","xxlarge9":"GridColumn-module_xxlarge9__3ohTx","xxlarge10":"GridColumn-module_xxlarge10__2vZpt","xxlarge11":"GridColumn-module_xxlarge11__2wqSX","xxlarge12":"GridColumn-module_xxlarge12__32JYc","gutter":"GridColumn-module_gutter__1FXZo"};

/**
 * @see {@link https://livingdesign.walmart.com/components/grid/react/}
 */

const GridColumn = props => {
  const _a = applyCommonProps(props, GridColumn, true),
        {
    children,
    className,
    hasGutter = false,
    lg,
    md,
    sm = 12,
    xl,
    xxl
  } = _a,
        rest = __rest(_a, ["children", "className", "hasGutter", "lg", "md", "sm", "xl", "xxl"]);

  return jsx("div", Object.assign({
    className: classNames(style$x.gridColumn, hasGutter && style$x.gutter, style$x[`small${sm}`], md && style$x[`medium${md}`], lg && style$x[`large${lg}`], xl && style$x[`xlarge${xl}`], xxl && style$x[`xxlarge${xxl}`], className)
  }, rest, {
    children: children
  }), void 0);
};
GridColumn.displayName = "GridColumn";

var style$w = {"iconButton":"IconButton-module_iconButton__SqVG-","small":"IconButton-module_small__1rTxy","medium":"IconButton-module_medium__2VvpC","large":"IconButton-module_large__3PFCV"};

const isAnchor = props => "href" in props;
/**
 * @see {@link https://livingdesign.walmart.com/components/icon-button/react/}
 */


const IconButton = React.forwardRef((props, ref) => {
  const _a = applyCommonProps(props, IconButton),
        {
    a11yLabel,
    children,
    className,
    size = "small"
  } = _a,
        rest = __rest(_a, ["a11yLabel", "children", "className", "size"]);

  invariant(!!a11yLabel, "`IconButton` accessibility violation. `IconButton` requires an `a11yLabel`.");
  const classes = classNames(style$w.iconButton, size === "large" && style$w.large, size === "medium" && style$w.medium, size === "small" && style$w.small, className);
  const content = React.cloneElement(children, {
    size
  });

  if (isAnchor(props)) {
    const _b = rest,
          {
      href,
      onClick,
      onKeyDown: anchorOnKeyDown,
      target
    } = _b,
          anchorProps = __rest(_b, ["href", "onClick", "onKeyDown", "target"]);

    const onKeyDown = onAnchorKeyDown({
      href,
      onClick,
      onKeyDown: anchorOnKeyDown
    });
    return jsx("a", Object.assign({
      "aria-label": a11yLabel,
      className: classes,
      href: href,
      onClick: onClick,
      onKeyDown: onKeyDown,
      ref: ref
    }, getTarget(target), anchorProps, {
      children: content
    }), void 0);
  }

  return jsx("button", Object.assign({
    "aria-label": a11yLabel,
    className: classes,
    ref: ref,
    type: "button"
  }, rest, {
    children: content
  }), void 0);
});
IconButton.displayName = "IconButton";

var styles = {"lineClamp":"LineClamp-module_lineClamp__1zf3_"};

/*
  We used to calculate this dynamically for whatever text style happened to be present.
  But since it's always Bogle, we've hardcoded it.
*/

const BOGLE_BASELINE_RATIO = 0.21739130434782605;

const getRelativeLineHeight = node => {
  const style = window.getComputedStyle(node);
  return parseFloat(style.lineHeight) / parseFloat(style.fontSize);
};
/**
 * A utility component that limits the number of lines of text visible.
 *
 * @see {@link https://livingdesign.walmart.com/utilities/line-clamp/react/}
 */


const LineClamp = props => {
  const _a = applyCommonProps(props, LineClamp, true),
        {
    className,
    lines = 2
  } = _a,
        rest = __rest(_a, ["className", "lines"]);

  const ref = React.createRef();
  const [descenderPadding, setDescenderPadding] = React.useState(0);
  React.useEffect(() => {
    /* istanbul ignore if: branch coverage fix */
    if (!ref.current) {
      /* istanbul ignore next */
      return;
    }

    const lineHeight = getRelativeLineHeight(ref.current);
    setDescenderPadding(Math.max(0, 1 - lineHeight + BOGLE_BASELINE_RATIO) / 2);
  }, [ref]);
  return jsx("span", Object.assign({
    className: classNames(className, styles.lineClamp),
    ref: ref,
    style: {
      WebkitLineClamp: lines,
      paddingBottom: `${descenderPadding}em`,
      marginBottom: `-${descenderPadding}em`
    }
  }, rest), void 0);
};
LineClamp.displayName = "LineClamp";

var style$v = {"list":"List-module_list__ieYxX","divider":"List-module_divider__1dNE0"};

const List = props => {
  const _a = applyCommonProps(props, List),
        {
    children,
    className
  } = _a,
        rest = __rest(_a, ["children", "className"]);

  const childrenCount = React.Children.count(children);
  return jsx("ul", Object.assign({
    className: classNames(style$v.list, className)
  }, rest, {
    children: React.Children.map(children, (child, index) => jsxs("li", {
      children: [child, index < childrenCount - 1 && jsx("div", Object.assign({
        className: style$v.divider
      }, {
        children: jsx(Divider, {}, void 0)
      }), void 0)]
    }, void 0))
  }), void 0);
};

var style$u = {"listItem":"ListItem-module_listItem__-czf1","container":"ListItem-module_container__3e7mw","title":"ListItem-module_title__1FoKE","textLabel":"ListItem-module_textLabel__3U_wY","leading":"ListItem-module_leading__3fUGO","trailing":"ListItem-module_trailing__1Ja7r"};

const ListItem = props => {
  const _a = applyCommonProps(props, ListItem),
        {
    children,
    className,
    leading,
    title,
    trailing
  } = _a,
        rest = __rest(_a, ["children", "className", "leading", "title", "trailing"]);

  return jsxs("span", Object.assign({
    className: classNames(style$u.listItem, className)
  }, rest, {
    children: [leading && jsx("span", Object.assign({
      className: style$u.leading
    }, {
      children: leading
    }), void 0), jsxs("div", Object.assign({
      className: style$u.container
    }, {
      children: [title && jsx(Subheading, Object.assign({
        UNSAFE_className: style$u.title
      }, {
        children: title
      }), void 0), jsx(Body, Object.assign({
        UNSAFE_className: style$u.textLabel
      }, {
        children: children
      }), void 0)]
    }), void 0), trailing && jsx("span", Object.assign({
      className: style$u.trailing
    }, {
      children: trailing
    }), void 0)]
  }), void 0);
};

var style$t = {"snackbar":"Snackbar-module_snackbar__qwQNS","transitionEnter":"Snackbar-module_transitionEnter__Wmg4i","transitionEnterActive":"Snackbar-module_transitionEnterActive__4iBV1","transitionExit":"Snackbar-module_transitionExit__3bLV1","transitionExitActive":"Snackbar-module_transitionExitActive__3e5nX","snackContainer":"Snackbar-module_snackContainer__110-0","snack":"Snackbar-module_snack__Rc-q6"};

var style$s = {"snack":"Snack-module_snack__3s298","message":"Snack-module_message__2NLPj","close":"Snack-module_close__3xHZX","icon":"Snack-module_icon__ePCVC"};

var style$r = {"action":"SnackActionButton-module_action__FMJoc"};

/**
 * @private
 */

const SnackActionButton = React.forwardRef((props, ref) => {
  const _a = applyCommonProps(props, SnackActionButton, true),
        {
    className
  } = _a,
        rest = __rest(_a, ["className"]);

  return jsx("button", Object.assign({
    className: classNames(style$r.action, className),
    ref: ref,
    type: "button"
  }, rest), void 0);
});

/**
 * @private
 */

const SnackCloseButton = React.forwardRef((props, ref) => {
  const _a = applyCommonProps(props, SnackCloseButton, true),
        {
    children,
    className
  } = _a,
        rest = __rest(_a, ["children", "className"]);

  return jsx("button", Object.assign({
    className: classNames(style$s.close, className),
    ref: ref,
    type: "button"
  }, rest, {
    children: jsx(Close, {
      className: style$s.icon
    }, void 0)
  }), void 0);
});

/**
 * @private
 */

const Snack = props => {
  const {
    actionButtonProps,
    className,
    message,
    onClose,
    closeButtonProps
  } = props,
        rest = __rest(props, ["actionButtonProps", "className", "message", "onClose", "closeButtonProps"]);

  const _a = actionButtonProps || {},
        {
    children: actionButtonLabel,
    onClick: actionButtonClick
  } = _a,
        actionButtonRest = __rest(_a, ["children", "onClick"]);

  const _b = closeButtonProps || {},
        {
    onClick: closeButtonClick
  } = _b,
        closeButtonRest = __rest(_b, ["onClick"]);

  return jsxs("div", Object.assign({
    className: classNames(style$s.snack, className)
  }, rest, {
    children: [jsx("div", Object.assign({
      className: style$s.message
    }, {
      children: message
    }), void 0), actionButtonProps && jsx(SnackActionButton, Object.assign({
      onClick: event => {
        actionButtonClick === null || actionButtonClick === void 0 ? void 0 : actionButtonClick(event);
        onClose(event);
      }
    }, actionButtonRest, {
      children: actionButtonLabel
    }), void 0), jsx(SnackCloseButton, Object.assign({
      "aria-label": "Close",
      onClick: event => {
        closeButtonClick === null || closeButtonClick === void 0 ? void 0 : closeButtonClick(event);
        onClose(event);
      }
    }, closeButtonRest), void 0)]
  }), void 0);
};

/**
 * This hook automatically initiates removal of the snackbar after a duration period. The period changes based on the length of the snack message.
 *
 * NOTE: This hook has been abstracted into a separate file to prevent
 * any `setTimeout` issues within glass and is not meant for reuse.
 * The implementation has been mocked within the glass `jestSetup`.
 *
 * @param snack
 * @param onClose
 */

const useRemoveSnackbar = (snack, onClose) => {
  React.useEffect(() => {
    /* istanbul ignore if */
    if (!snack) {
      return () => undefined;
    }

    const {
      message
    } = snack;
    const duration = message.length > 120 ? 3500 + (message.length - 120) * 60 : 3500;
    const timeoutID = setTimeout(() => {
      onClose();
    }, duration);
    return () => {
      clearTimeout(timeoutID);
    };
  }, [snack, onClose]);
};

/**
 * @private
 */

const Snackbar = props => {
  const {
    snack,
    setSnack
  } = props;

  const {
    actionButtonProps,
    closeButtonProps,
    onClose: snackOnClose
  } = snack,
        snackRest = __rest(snack, ["actionButtonProps", "closeButtonProps", "onClose"]);

  const snackbarRef = React.useRef(null);
  const [inProp, setInProp] = React.useState(false);

  const onClose = event => {
    setInProp(false);
    snackOnClose === null || snackOnClose === void 0 ? void 0 : snackOnClose(event);
  }; // ensure that snackbar do not propogate to overlay components. if stop propagation
  // is preventing clicks from being captured, a new event must be specified with
  // `useCapture` that is declared before this component is rendered.
  // @see {@link https://jira.walmart.com/browse/LD-1260}


  useOnClickInside(snackbarRef, event => event.stopPropagation(), true); // remove snackbar after X duration

  useRemoveSnackbar(snack, onClose); // new animation if snack is received

  React.useEffect(() => {
    setInProp(true);
  }, [snack]);
  return jsx(CSSTransition, Object.assign({
    classNames: {
      enter: style$t.transitionEnter,
      enterActive: style$t.transitionEnterActive,
      exit: style$t.transitionExit,
      exitActive: style$t.transitionExitActive
    },
    in: inProp,
    mountOnEnter: true,
    onExited: () => setSnack(undefined),
    timeout: {
      enter: 500,
      exit: 750
    }
  }, {
    children: jsx("div", Object.assign({
      className: style$t.snackbar,
      ref: snackbarRef
    }, {
      children: jsx("div", Object.assign({
        className: style$t.snackContainer
      }, {
        children: jsx(Snack, Object.assign({
          actionButtonProps: actionButtonProps,
          className: style$t.snack,
          closeButtonProps: closeButtonProps,
          onClose: onClose
        }, snackRest), void 0)
      }), void 0)
    }), void 0)
  }), void 0);
};

const SnackbarContext = React.createContext({
  addSnack:
  /* istanbul ignore next */
  () => undefined
});
/**
 * Snackbars provide brief messages at the bottom of the screen regarding app processes. Snackbars contain brief text directly related to the operation performed. They may contain an action, but no icons.
 *
 * @see {@link https://livingdesign.walmart.com/components/snackbar/react/}
 */

const SnackbarProvider = props => {
  const {
    children
  } = props;
  const [snack, setSnack] = React.useState();
  const value = React.useRef({
    addSnack: setSnack
  });
  return jsxs(SnackbarContext.Provider, Object.assign({
    value: value.current
  }, {
    children: [children, jsx("div", Object.assign({
      "aria-live": "assertive"
    }, {
      children: snack && jsx(Snackbar, {
        snack: snack,
        setSnack: setSnack
      }, void 0)
    }), void 0)]
  }), void 0);
};
SnackbarProvider.displayName = "SnackbarProvider";

/**
 * Snackbars provide brief messages at the bottom of the screen regarding app processes. Snackbars contain brief text directly related to the operation performed. They may contain an action, but no icons.
 *
 * @see {@link https://livingdesign.walmart.com/components/snackbar/react/}
 */

const useSnackbar = () => React.useContext(SnackbarContext);

const LivingDesignProvider = props => {
  const {
    children
  } = props;
  return jsx(SSRProvider, {
    children: jsx(A11yAnnouncementProvider, {
      children: jsx(SnackbarProvider, {
        children: children
      }, void 0)
    }, void 0)
  }, void 0);
};

var style$q = {"container":"ModalPortal-module_container__1x9pQ","modal":"ModalPortal-module_modal__PQCZq","small":"ModalPortal-module_small__1oaK3","medium":"ModalPortal-module_medium__Sjd6c","large":"ModalPortal-module_large__2qweU","transitionEnter":"ModalPortal-module_transitionEnter__17yeA","transitionEnterActive":"ModalPortal-module_transitionEnterActive__2t76n","transitionExit":"ModalPortal-module_transitionExit__3-IoU","transitionExitActive":"ModalPortal-module_transitionExitActive__2lTZ1","header":"ModalPortal-module_header__1wQdB","title":"ModalPortal-module_title__2Go7J","content":"ModalPortal-module_content__1n3pH","contentInner":"ModalPortal-module_contentInner__1vBc3","actions":"ModalPortal-module_actions__2d5El","scrim":"ModalPortal-module_scrim__hS5Zq"};

/**
 * @private
 */

const ModalPortal = props => {
  const _a = applyCommonProps(props, ModalPortal, true),
        {
    actions,
    children,
    closeButtonProps,
    isOpen,
    onClose,
    size = "small",
    title,
    onClosed
  } = _a,
        rest = __rest(_a, ["actions", "children", "closeButtonProps", "isOpen", "onClose", "size", "title", "onClosed"]);

  invariant(!!title, "Required property `title` not provided.");

  const _b = closeButtonProps || {},
        {
    onClick: closeButtonOnClick
  } = _b,
        closeButtonRest = __rest(_b, ["onClick"]);

  const modalRef = React.useRef(null);
  const [setScrollableContentElement] = useLockBodyScroll();
  const titleId = useSSRSafeId();
  useOnClickOutside(modalRef, onClose);
  useOnKeyDown(["Esc", "Escape"], onClose);
  return jsx(CSSTransition, Object.assign({
    classNames: {
      enter: style$q.transitionEnter,
      enterActive: style$q.transitionEnterActive,
      exit: style$q.transitionExit,
      exitActive: style$q.transitionExitActive
    },
    in: isOpen,
    mountOnEnter: true,
    onExited: () => onClosed === null || onClosed === void 0 ? void 0 : onClosed(),
    timeout: 500
  }, {
    children: jsxs(Overlay, Object.assign({}, rest, {
      children: [jsx("div", Object.assign({
        className: style$q.container
      }, {
        children: jsxs("div", Object.assign({
          "aria-labelledby": titleId,
          className: classNames(style$q.modal, size === "large" && style$q.large, size === "medium" && style$q.medium, size === "small" && style$q.small),
          ref: modalRef,
          role: "dialog"
        }, {
          children: [jsxs("div", Object.assign({
            className: style$q.header
          }, {
            children: [jsx(OverlayCloseButton, Object.assign({
              "aria-label": "Close dialog",
              onClick: event => {
                closeButtonOnClick === null || closeButtonOnClick === void 0 ? void 0 : closeButtonOnClick(event);
                onClose(event);
              }
            }, closeButtonRest), void 0), jsx("h2", Object.assign({
              className: style$q.title,
              id: titleId
            }, {
              children: title
            }), void 0)]
          }), void 0), jsx("div", Object.assign({
            className: style$q.content,
            ref: setScrollableContentElement
          }, {
            children: jsx("div", Object.assign({
              className: style$q.contentInner
            }, {
              children: children
            }), void 0)
          }), void 0), actions && // NOTE: Safari needs a wrapping block element, otherwise a flex container
          // renders with no height.
          jsx("div", Object.assign({
            className: style$q.actions
          }, {
            children: actions
          }), void 0)]
        }), void 0)
      }), void 0), jsx(OverlayScrim, {
        className: style$q.scrim
      }, void 0)]
    }), void 0)
  }), void 0);
};

/**
 * Modals focus the user’s attention on a single task in a window that sits on top of the page content.
 *
 * @see {@link https://livingdesign.walmart.com/components/modal/react/}
 */

const Modal = props => {
  const {
    isOpen,
    onClosed
  } = props,
        rest = __rest(props, ["isOpen", "onClosed"]);

  const [isTransitioning, setIsTransitioning] = React.useState(isOpen);
  const previousIsOpen = React.useRef(isOpen);
  /**
   * This hook delays `isOpen` property changes such that the
   * `ModalPortal` component can transition in and out.
   */

  React.useEffect(() => {
    if (previousIsOpen.current === isOpen) {
      return;
    }

    previousIsOpen.current = isOpen;
    const handle = setTimeout(() => {
      setIsTransitioning(true);
    }, 0);
    /* eslint-disable-next-line consistent-return */

    return () => clearTimeout(handle);
  }, [isOpen]);

  if (!isOpen && !isTransitioning) {
    return null;
  }

  return jsx(ModalPortal, Object.assign({
    isOpen: isOpen && isTransitioning,
    onClosed: () => {
      setIsTransitioning(false);
      onClosed === null || onClosed === void 0 ? void 0 : onClosed();
    }
  }, rest), void 0);
};
Modal.displayName = "Modal";

var style$p = {"nudge":"Nudge-module_nudge__2v5GZ","wrapperOuter":"Nudge-module_wrapperOuter__RP9X9","hasCloseButton":"Nudge-module_hasCloseButton__2odZk","hasNoContent":"Nudge-module_hasNoContent__13J7U","leading":"Nudge-module_leading__1KIGt","wrapperInner":"Nudge-module_wrapperInner__d-P5j","title":"Nudge-module_title__Yrw-y","description":"Nudge-module_description__1LDSy","actions":"Nudge-module_actions__2xGbf"};

var style$o = {"closeButton":"NudgeCloseButton-module_closeButton__28Qsu"};

/**
 * @private
 */

const NudgeCloseButton = React.forwardRef((props, ref) => {
  const _a = applyCommonProps(props, NudgeCloseButton),
        {
    children,
    className
  } = _a,
        rest = __rest(_a, ["children", "className"]);

  return jsx("button", Object.assign({
    className: classNames(style$o.closeButton, className),
    ref: ref,
    type: "button"
  }, rest, {
    children: jsx(Close, {
      "aria-hidden": true,
      size: "medium"
    }, void 0)
  }), void 0);
});

/**
 * Nudges are used to remind, provide instructions or prompts for the next-step in a process, or as a mechanism to upsell or cross-sell. The information they provide should not be required for a user to complete a task.
 *
 * @see {@link https://livingdesign.walmart.com/components/nudge/react/}
 */

const Nudge = props => {
  const _a = applyCommonProps(props, Nudge),
        {
    actions,
    closeButtonProps = {},
    children,
    className,
    leading,
    onClose,
    title
  } = _a,
        rest = __rest(_a, ["actions", "closeButtonProps", "children", "className", "leading", "onClose", "title"]);

  return jsxs("div", Object.assign({
    className: classNames(style$p.nudge, onClose && style$p.hasCloseButton, !actions && !children && style$p.hasNoContent, className)
  }, rest, {
    children: [jsxs("div", Object.assign({
      className: style$p.wrapperOuter
    }, {
      children: [leading && jsx("div", Object.assign({
        className: style$p.leading
      }, {
        children: leading
      }), void 0), jsxs("div", Object.assign({
        className: style$p.wrapperInner
      }, {
        children: [jsx("span", Object.assign({
          className: style$p.title
        }, {
          children: title
        }), void 0), children && jsx("div", Object.assign({
          className: style$p.description
        }, {
          children: children
        }), void 0), actions && jsx("div", Object.assign({
          className: style$p.actions
        }, {
          children: actions
        }), void 0)]
      }), void 0)]
    }), void 0), onClose && jsx(NudgeCloseButton, Object.assign({
      "aria-label": "Close"
    }, closeButtonProps, {
      onClick: event => {
        var _a;

        (_a = closeButtonProps === null || closeButtonProps === void 0 ? void 0 : closeButtonProps.onClick) === null || _a === void 0 ? void 0 : _a.call(closeButtonProps, event);
        onClose(event);
      }
    }), void 0)]
  }), void 0);
};
Nudge.displayName = "Nudge";

var style$n = {"container":"PanelPortal-module_container__3Kdr0","right":"PanelPortal-module_right__2Dq1y","left":"PanelPortal-module_left__OgRkv","panel":"PanelPortal-module_panel__2Q0z1","small":"PanelPortal-module_small__1SMOf","medium":"PanelPortal-module_medium__nkZYh","large":"PanelPortal-module_large__1MnGU","header":"PanelPortal-module_header__3umNh","title":"PanelPortal-module_title__3GrwQ","content":"PanelPortal-module_content__2C73t","contentInner":"PanelPortal-module_contentInner__2AtqI","actions":"PanelPortal-module_actions__3ODSv"};

/**
 * @private
 */

const PanelPortal = props => {
  const _a = applyCommonProps(props, PanelPortal, true),
        {
    actions,
    children,
    onClose,
    closeButtonProps,
    position = "left",
    size = "small",
    title
  } = _a,
        rest = __rest(_a, ["actions", "children", "onClose", "closeButtonProps", "position", "size", "title"]);

  invariant(!!title, "Required property `title` not provided.");

  const _b = closeButtonProps || {},
        {
    onClick: closeButtonOnClick
  } = _b,
        closeButtonRest = __rest(_b, ["onClick"]);

  const panelRef = React.useRef(null);
  const titleId = useSSRSafeId(); // NOTE: On Safari iOS, body-scroll-lock locks scrolling on all elements
  // except for the passed in ref. Need to pass the element that can scroll.
  // https://github.com/reakit/reakit/issues/469#issuecomment-549013422

  const [setScrollableContentElement] = useLockBodyScroll();
  useOnClickOutside(panelRef, onClose);
  useOnKeyDown(["Esc", "Escape"], onClose);
  return jsxs(Overlay, Object.assign({}, rest, {
    children: [jsx("div", Object.assign({
      className: classNames(style$n.container, position === "left" && style$n.left, position === "right" && style$n.right)
    }, {
      children: jsxs("div", Object.assign({
        "aria-labelledby": titleId,
        className: classNames(style$n.panel, size === "small" && style$n.small, size === "medium" && style$n.medium, size === "large" && style$n.large),
        ref: panelRef,
        role: "dialog"
      }, {
        children: [typeof title === "function" && title({
          titleId
        }), typeof title !== "function" && jsxs("div", Object.assign({
          className: style$n.header
        }, {
          children: [jsx(OverlayCloseButton, Object.assign({
            "aria-label": "Close panel",
            onClick: event => {
              closeButtonOnClick === null || closeButtonOnClick === void 0 ? void 0 : closeButtonOnClick(event);
              onClose(event);
            }
          }, closeButtonRest), void 0), jsx("h2", Object.assign({
            className: style$n.title,
            id: titleId
          }, {
            children: title
          }), void 0)]
        }), void 0), jsx("div", Object.assign({
          className: style$n.content,
          ref: setScrollableContentElement
        }, {
          children: jsx("div", Object.assign({
            className: style$n.contentInner
          }, {
            children: children
          }), void 0)
        }), void 0), actions && jsx("div", Object.assign({
          className: style$n.actions
        }, {
          children: actions
        }), void 0)]
      }), void 0)
    }), void 0), jsx(OverlayScrim, {}, void 0)]
  }), void 0);
};

/**
 * A panel provides supplemental information or form inputs that relate to your primary canvas.
 *
 * @see {@link https://livingdesign.walmart.com/components/panel/react/}
 */

const Panel = props => {
  const {
    isOpen = false
  } = props,
        rest = __rest(props, ["isOpen"]);

  if (!isOpen) {
    return null;
  }

  return jsx(PanelPortal, Object.assign({}, rest), void 0);
};
Panel.displayName = "Panel";

var style$m = {"popover":"Popover-module_popover__3P1BT"};

var style$l = {"layoutManager":"PopoverLayoutManager-module_layoutManager__lYGx8","top":"PopoverLayoutManager-module_top__21e0p","right":"PopoverLayoutManager-module_right__2V8HZ","bottom":"PopoverLayoutManager-module_bottom__1iLrH","left":"PopoverLayoutManager-module_left__1L07v"};

var style$k = {"popover":"BasePopover-module_popover__LHp9-","nubbin":"BasePopover-module_nubbin__TfPNa","top":"BasePopover-module_top__2qQh2","right":"BasePopover-module_right__1Q3AQ","bottom":"BasePopover-module_bottom__3crAV","left":"BasePopover-module_left__3FAN-","content":"BasePopover-module_content__1qiD7"};

/**
 * @private
 */

const BasePopover = props => {
  const {
    children,
    className,
    nubbin
  } = props,
        rest = __rest(props, ["children", "className", "nubbin"]);

  return jsx("div", Object.assign({
    className: classNames(style$k.popover, nubbin && style$k.nubbin, nubbin === "top" && style$k.top, nubbin === "right" && style$k.right, nubbin === "left" && style$k.left, nubbin === "bottom" && style$k.bottom, className)
  }, rest, {
    children: jsx("div", Object.assign({
      className: style$k.content
    }, {
      children: children
    }), void 0)
  }), void 0);
};

/**
 * @private
 */

const FocusGuard = props => {
  const {
    children,
    onGuard
  } = props,
        rest = __rest(props, ["children", "onGuard"]);

  const ref = React.useRef(null);
  React.useEffect(() => {
    var _a; // istanbul ignore next


    const focusableNodes = ((_a = ref.current) === null || _a === void 0 ? void 0 : _a.querySelectorAll( // @see {@link https://github.com/theKashey/focus-lock/blob/125a50bc53fdc2cbb7705327790ecff4f3289ba1/src/utils/tabbables.ts#L4-L24}
    "a[href], button:enabled, input:enabled:not([type=hidden]), select:enabled, textarea:enabled, [tabindex], [autofocus]")) || []; // NOTE: first and last node are focus guards, second node will be the first focusable item.

    setTimeout(() => focusableNodes[1].focus(), 0);
  }, []);
  return jsxs("div", Object.assign({
    ref: ref
  }, rest, {
    children: [jsx("div", {
      "aria-hidden": true,
      tabIndex: 0,
      onFocus: onGuard
    }, void 0), jsx("div", {
      "aria-hidden": true,
      tabIndex: -1
    }, void 0), children, jsx("div", {
      "aria-hidden": true,
      tabIndex: 0,
      onFocus: onGuard
    }, void 0)]
  }), void 0);
};

const getPopoverPositionStyle = ({
  popoverRef,
  position,
  triggerRef
}) => getPositionStyle({
  calculatePosition({
    referrerHeight,
    referrerWidth,
    targetHeight,
    targetWidth
  }) {
    let left;

    if (position === "left") {
      left = -1 * referrerWidth;
    } else if (position === "right") {
      left = targetWidth;
    } else {
      left = (targetWidth - referrerWidth) / 2;
    }

    let top;

    if (position === "bottom") {
      top = targetHeight;
    } else if (position === "top") {
      top = -1 * referrerHeight;
    } else {
      top = (targetHeight - referrerHeight) / 2;
    }

    return {
      left,
      top
    };
  },

  referrerRef: popoverRef,
  targetRef: triggerRef
});

/**
 * @private
 */

const PopoverLayoutManager = props => {
  const {
    basePopoverProps = {},
    className,
    content,
    hasNubbin = false,
    onClose,
    position = "bottom",
    triggerRef
  } = props,
        rest = __rest(props, ["basePopoverProps", "className", "content", "hasNubbin", "onClose", "position", "triggerRef"]);

  const [popoverStyle, setPopoverStyle] = React.useState({
    // NOTE: hide the popover until it has been mounted and positioned
    visibility: "hidden"
  });
  const surfaceRef = React.useRef(null);
  const onCloseWithFocusReturn = React.useCallback(event => {
    var _a;

    (_a = triggerRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    onClose(event);
  }, [triggerRef, onClose]);
  useOnClickOutside([surfaceRef, triggerRef], onClose);
  useOnKeyDown(["Esc", "Escape"], onCloseWithFocusReturn);
  React.useEffect(() => {
    const positionStyle = getPopoverPositionStyle({
      popoverRef: surfaceRef,
      position,
      triggerRef
    });
    setPopoverStyle(positionStyle);
  }, [surfaceRef, position, triggerRef]);
  React.useEffect(() => {
    const onResize = () => {
      const positionStyle = getPopoverPositionStyle({
        popoverRef: surfaceRef,
        position,
        triggerRef
      });
      setPopoverStyle(positionStyle);
    };

    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
    };
  }, [surfaceRef, position, triggerRef]);
  const nubbin = {
    bottom: "top",
    left: "right",
    right: "left",
    top: "bottom"
  }[position];
  return jsx("div", Object.assign({
    className: classNames(style$l.layoutManager, position === "top" && style$l.top, position === "right" && style$l.right, position === "bottom" && style$l.bottom, position === "left" && style$l.left, className),
    ref: surfaceRef,
    style: popoverStyle
  }, rest, {
    children: jsx(FocusGuard, Object.assign({
      onGuard: onCloseWithFocusReturn
    }, {
      children: jsx(BasePopover, Object.assign({}, hasNubbin && {
        nubbin
      }, basePopoverProps, {
        children: content
      }), void 0)
    }), void 0)
  }), void 0);
};

/**
 * Popovers are overlays that display contextual content on demand.
 *
 * @see {@link https://livingdesign.walmart.com/components/popover/react/}
 */

const Popover = props => {
  const _a = applyCommonProps(props, Popover),
        {
    basePopoverProps,
    children,
    className,
    content,
    hasNubbin,
    isOpen = false,
    onClose,
    position,
    triggerRef
  } = _a,
        rest = __rest(_a, ["basePopoverProps", "children", "className", "content", "hasNubbin", "isOpen", "onClose", "position", "triggerRef"]);

  const child = React.Children.only(children);
  const surfaceId = useSSRSafeId();
  return jsxs("span", Object.assign({
    className: classNames(style$m.popover, className)
  }, rest, {
    children: [React.cloneElement(child, {
      "aria-controls": surfaceId,
      "aria-expanded": isOpen
    }), jsx("div", Object.assign({
      id: surfaceId
    }, {
      children: isOpen && jsx(PopoverLayoutManager, {
        basePopoverProps: basePopoverProps,
        content: content,
        hasNubbin: hasNubbin,
        onClose: onClose,
        position: position,
        triggerRef: triggerRef
      }, void 0)
    }), void 0)]
  }), void 0);
};
Popover.displayName = "Popover";

var style$j = {"progressIndicator":"ProgressIndicator-module_progressIndicator__3dWAE","track":"ProgressIndicator-module_track__u_d8A","trackFill":"ProgressIndicator-module_trackFill__3nI_Q","error":"ProgressIndicator-module_error__3Ou9n","info":"ProgressIndicator-module_info__2h1r6","success":"ProgressIndicator-module_success__2bV0w","warning":"ProgressIndicator-module_warning__2RsOg","labelContainer":"ProgressIndicator-module_labelContainer__pcxAH","label":"ProgressIndicator-module_label__2D4pM","valueLabel":"ProgressIndicator-module_valueLabel__2whoy"};

/**
 * Progress Indicators display a visual representation of the completion of a task or process.
 *
 * @see {@link https://livingdesign.walmart.com/components/progress-indicator/react/}
 */

const ProgressIndicator = props => {
  const _a = applyCommonProps(props, ProgressIndicator, false, true),
        {
    a11yLabelledBy,
    className,
    label,
    max = 100,
    min = 0,
    value = 0,
    valueLabel,
    variant = "info"
  } = _a,
        rest = __rest(_a, ["a11yLabelledBy", "className", "label", "max", "min", "value", "valueLabel", "variant"]);

  const labelCount = (label ? 1 : 0) + (a11yLabelledBy ? 1 : 0) === 1;
  invariant(labelCount, "`ProgressIndicator` accessibility violation. `ProgressIndicator` requires a `label` OR an `a11yLabelledBy`.");
  const labelId = useSSRSafeId();
  const hasLabel = label || valueLabel;
  return jsxs("span", Object.assign({
    "aria-valuemax": max,
    "aria-valuemin": min,
    "aria-valuenow": value,
    className: classNames(style$j.progressIndicator, className),
    role: "progressbar"
  }, a11yLabelledBy && {
    "aria-labelledby": a11yLabelledBy
  }, label && {
    "aria-labelledby": labelId
  }, valueLabel && {
    "aria-valuetext": valueLabel
  }, rest, {
    children: [jsx("span", Object.assign({
      className: style$j.track
    }, {
      children: jsx("span", {
        className: classNames(style$j.trackFill, variant === "error" && style$j.error, variant === "info" && style$j.info, variant === "success" && style$j.success, variant === "warning" && style$j.warning),
        // eslint-disable-next-line react/forbid-dom-props
        style: {
          width: `${(value - min) / (max - min) * 100}%`
        }
      }, void 0)
    }), void 0), hasLabel && jsxs("span", Object.assign({
      className: style$j.labelContainer
    }, {
      children: [jsx("span", Object.assign({
        className: style$j.label
      }, label && {
        id: labelId
      }, {
        children: label
      }), void 0), jsx("span", Object.assign({
        className: style$j.valueLabel
      }, {
        children: valueLabel
      }), void 0)]
    }), void 0)]
  }), void 0);
};
ProgressIndicator.displayName = "ProgressIndicator";

var style$i = {"item":"ProgressTrackerItem-module_item__2F2Je","hasLabel":"ProgressTrackerItem-module_hasLabel__3BsLB","isCurrent":"ProgressTrackerItem-module_isCurrent__3mcSV","step":"ProgressTrackerItem-module_step__X3cQ2","stepInner":"ProgressTrackerItem-module_stepInner__C_hRG","isActive":"ProgressTrackerItem-module_isActive__1w0Ui","error":"ProgressTrackerItem-module_error__1Rhz0","info":"ProgressTrackerItem-module_info__3BijR","success":"ProgressTrackerItem-module_success__2t82m","warning":"ProgressTrackerItem-module_warning__lmt-B","stepOuter":"ProgressTrackerItem-module_stepOuter__87SLB"};

var style$h = {"progressTracker":"ProgressTracker-module_progressTracker__3pAzT","trackContainer":"ProgressTracker-module_trackContainer__1tzDH","track":"ProgressTracker-module_track__3pRBg","indicator":"ProgressTracker-module_indicator__3J-ey","error":"ProgressTracker-module_error__H5HsV","info":"ProgressTracker-module_info__3j-AZ","success":"ProgressTracker-module_success__2SLK7","warning":"ProgressTracker-module_warning__2wylH","items":"ProgressTracker-module_items__3vKxc"};

/**
 * Progress tracker is a visual representation of a users progress through a set of steps. They inform the user of the number of steps required to complete a specified process.
 *
 * @see {@link https://livingdesign.walmart.com/components/progress-tracker/react/}
 */

const ProgressTracker = props => {
  const _a = applyCommonProps(props, ProgressTracker, true),
        {
    activeIndex = 0,
    children,
    className,
    variant = "info"
  } = _a,
        rest = __rest(_a, ["activeIndex", "children", "className", "variant"]);

  const lastIndex = React__default.Children.count(children) - 1;
  let clampedIndex;

  if (lastIndex >= 0 && activeIndex > lastIndex) {
    clampedIndex = lastIndex;
  } else if (activeIndex < 0) {
    clampedIndex = 0;
  } else {
    clampedIndex = activeIndex;
  }

  return jsxs("div", Object.assign({
    className: classNames(style$h.progressTracker, variant === "error" && style$h.error, variant === "info" && style$h.info, variant === "success" && style$h.success, variant === "warning" && style$h.warning, className)
  }, rest, {
    children: [jsx("div", Object.assign({
      className: style$h.trackContainer
    }, {
      children: jsx("span", Object.assign({
        className: style$h.track
      }, {
        children: jsx("span", {
          className: style$h.indicator,
          style: {
            width: `${100 * clampedIndex / lastIndex}%`
          }
        }, void 0)
      }), void 0)
    }), void 0), jsx("div", Object.assign({
      className: style$h.items,
      role: "list"
    }, {
      children: React__default.Children.map(children, (child, index) => {
        if (!React__default.isValidElement(child)) {
          return null;
        }

        return React__default.cloneElement(child, Object.assign(Object.assign(Object.assign({}, child.props), activeIndex === index ? {
          "aria-current": "step"
        } : {}), {
          className: classNames(activeIndex >= index && style$i.isActive, activeIndex === index && style$i.isCurrent, variant === "error" && style$i.error, variant === "info" && style$i.info, variant === "success" && style$i.success, variant === "warning" && style$i.warning, child.props.className),
          key: index,
          role: "listitem",
          style: Object.assign(Object.assign({}, child.props.style), {
            width: `${index === 0 || index === lastIndex ? 50 / lastIndex : 100 / lastIndex}%`
          })
        }));
      })
    }), void 0)]
  }), void 0);
};
ProgressTracker.displayName = "ProgressTracker";

/**
 * @see {@link https://livingdesign.walmart.com/components/progress-tracker/react/}
 */

const ProgressTrackerItem = props => {
  const _a = applyCommonProps(props, ProgressTrackerItem, true),
        {
    children,
    className
  } = _a,
        rest = __rest(_a, ["children", "className"]);

  return jsxs("span", Object.assign({
    className: classNames(style$i.item, children && style$i.hasLabel, className)
  }, rest, {
    children: [jsxs("svg", Object.assign({
      "aria-hidden": true,
      className: style$i.step,
      viewBox: "0 0 16 16",
      xmlns: "http://www.w3.org/2000/svg"
    }, {
      children: [jsx("circle", {
        className: style$i.stepOuter,
        cx: "8",
        cy: "8",
        r: "7"
      }, void 0), jsx("circle", {
        className: style$i.stepInner,
        cx: "8",
        cy: "8",
        r: "4"
      }, void 0)]
    }), void 0), children]
  }), void 0);
};
ProgressTrackerItem.displayName = "ProgressTrackerItem";

var style$g = {"label":"Radio-module_label__1Vk5v","checked":"Radio-module_checked__3cVgt","disabled":"Radio-module_disabled__33SyS","input":"Radio-module_input__nvkea","radioInput":"Radio-module_radioInput__dWgYr","radioInputOuter":"Radio-module_radioInputOuter__2VGgg","radioInputInner":"Radio-module_radioInputInner__1y-6l","labelText":"Radio-module_labelText__3awah"};

/**
 * Radio buttons represent a group of mutually exclusive choices, compared to checkboxes that allow users to make one or more selections from a group. In use cases where only one selection of a group is allowed, use the radio button component instead of the checkbox.
 *
 * @see {@link https://livingdesign.walmart.com/components/radio/react/}
 */

const Radio = React.forwardRef((props, ref) => {
  const _a = applyCommonProps(props, Radio, true),
        {
    a11yLabelledBy,
    checked = false,
    className,
    disabled = false,
    id: initialId,
    label,
    name,
    onChange,
    radioProps = {},
    value
  } = _a,
        rest = __rest(_a, ["a11yLabelledBy", "checked", "className", "disabled", "id", "label", "name", "onChange", "radioProps", "value"]);

  const labelCount = (label ? 1 : 0) + (a11yLabelledBy ? 1 : 0) === 1;
  invariant(labelCount, "`Radio` accessibility violation. `Radio` requires a `label` OR an `a11yLabelledBy`.");

  const {
    className: radioClassName
  } = radioProps,
        radioRest = __rest(radioProps, ["className"]);

  const id = useSSRSafeId(initialId);
  return jsxs("label", Object.assign({
    className: classNames(style$g.label, checked && style$g.checked, disabled && style$g.disabled, className),
    htmlFor: id
  }, rest, {
    children: [jsx("input", Object.assign({
      checked: checked,
      className: classNames(style$g.input, radioClassName),
      disabled: disabled,
      id: id,
      name: name,
      onChange: onChange,
      ref: ref,
      type: "radio"
    }, a11yLabelledBy && {
      "aria-labelledby": a11yLabelledBy
    }, value && {
      value
    }, radioRest), void 0), jsxs("svg", Object.assign({
      "aria-hidden": true,
      className: style$g.radioInput,
      viewBox: "0 0 20 20",
      xmlns: "http://www.w3.org/2000/svg"
    }, {
      children: [jsx("circle", {
        className: style$g.radioInputOuter,
        cx: "10",
        cy: "10"
      }, void 0), jsx("circle", {
        className: style$g.radioInputInner,
        cx: "10",
        cy: "10"
      }, void 0)]
    }), void 0), label && jsx("span", Object.assign({
      className: style$g.labelText
    }, {
      children: label
    }), void 0)]
  }), void 0);
});
Radio.displayName = "Radio";

var style$f = {"rating":"Rating-module_rating__ydxDG","iconSmall":"Rating-module_iconSmall__2g4Lc","iconLarge":"Rating-module_iconLarge__1aeJh"};

/**
 * Ratings provide insight into how well a product or service has been received by those who have bought or used it previously.
 *
 * @see {@link https://livingdesign.walmart.com/components/rating/react/}
 */

const Rating = props => {
  const _a = applyCommonProps(props, Rating, true),
        {
    className,
    size = "small",
    value = 0
  } = _a,
        rest = __rest(_a, ["className", "size", "value"]);

  const iconProps = {
    "aria-hidden": true,
    className: classNames(size === "small" && style$f.iconSmall, size === "large" && style$f.iconLarge)
  };
  return jsx("div", Object.assign({
    className: classNames(style$f.rating, className)
  }, rest, {
    children: [1, 2, 3, 4, 5].map(count => {
      if (value >= count - 0.25) {
        return jsx(StarFill, Object.assign({}, iconProps), count);
      } else if (value >= count - 0.75) {
        return jsx(StarHalf, Object.assign({}, iconProps), count);
      }

      return jsx(Star, Object.assign({}, iconProps), count);
    })
  }), void 0);
};
Rating.displayName = "Rating";

var style$e = {"container":"Select-module_container__1GRXc","select":"Select-module_select__10Xfl","error":"Select-module_error__TDfuE","icon":"Select-module_icon__2sNPn"};

/**
 * Select gives users the ability to make a single selection from a number of predefined options. It is usually found in forms.
 *
 * @see {@link https://livingdesign.walmart.com/components/select/react/}
 */

const Select = React.forwardRef((props, ref) => {
  const _a = applyCommonProps(props, Select, true),
        {
    children,
    className,
    disabled = false,
    error,
    helperText,
    id: initialId,
    label,
    onChange,
    selectProps: initialSelectProps,
    value
  } = _a,
        rest = __rest(_a, ["children", "className", "disabled", "error", "helperText", "id", "label", "onChange", "selectProps", "value"]);

  const [isFocused, setIsFocused] = React.useState(false);
  const id = useSSRSafeId(initialId);
  const helperId = `${id}_helper`;
  const selectProps = Object.assign(Object.assign(Object.assign({
    disabled,
    id,
    onChange,
    value
  }, (error || helperText) && {
    "aria-describedby": helperId
  }), initialSelectProps), {
    className: classNames(style$e.select, error && style$e.error, initialSelectProps === null || initialSelectProps === void 0 ? void 0 : initialSelectProps.className),

    onBlur(event) {
      var _a;

      setIsFocused(false);
      (_a = initialSelectProps === null || initialSelectProps === void 0 ? void 0 : initialSelectProps.onBlur) === null || _a === void 0 ? void 0 : _a.call(initialSelectProps, event);
    },

    onFocus(event) {
      var _a;

      setIsFocused(true);
      (_a = initialSelectProps === null || initialSelectProps === void 0 ? void 0 : initialSelectProps.onFocus) === null || _a === void 0 ? void 0 : _a.call(initialSelectProps, event);
    }

  });
  return jsxs("div", Object.assign({
    className: classNames(style$e.container, className)
  }, rest, {
    children: [jsx(FormLabel, Object.assign({
      disabled: disabled,
      htmlFor: id,
      isFocused: isFocused
    }, {
      children: label
    }), void 0), jsx("select", Object.assign({}, selectProps, {
      ref: ref
    }, {
      children: children
    }), void 0), jsx(CaretDown, {
      className: style$e.icon,
      size: "medium"
    }, void 0), (error || helperText) && jsxs(FormHelperText, Object.assign({
      id: helperId
    }, {
      children: [error && jsx(FormError, {
        children: error
      }, void 0), !error && jsx("span", {
        children: helperText
      }, void 0)]
    }), void 0)]
  }), void 0);
});
Select.displayName = "Select";

var style$d = {"sideNavigation":"SideNavigation-module_sideNavigation__1FLWJ","list":"SideNavigation-module_list__1PGSf"};

/**
 * Side navigation provides a list of primary links that navigate users to a URL. Links should be grouped together in sections based on related content or categories. A section title can proceed a list of links.
 *
 * @see {@link https://livingdesign.walmart.com/components/side-navigation/react/}
 */

const SideNavigation = props => {
  const _a = applyCommonProps(props, SideNavigation, true),
        {
    children,
    className
  } = _a,
        rest = __rest(_a, ["children", "className"]);

  return jsx("nav", Object.assign({
    className: classNames(style$d.sideNavigation, className)
  }, rest, {
    children: jsx("ul", Object.assign({
      className: style$d.list
    }, {
      children: React.Children.map(children, (child, index) => jsx("li", {
        children: child
      }, index))
    }), void 0)
  }), void 0);
};
SideNavigation.displayName = "SideNavigation";

var style$c = {"sideNavigationItem":"SideNavigationItem-module_sideNavigationItem__3PFuC","isCurrent":"SideNavigationItem-module_isCurrent__1vi88","leading":"SideNavigationItem-module_leading__-aAFR"};

/**
 * @see {@link https://livingdesign.walmart.com/components/side-navigation/react/}
 */

const SideNavigationItem = React.forwardRef((props, ref) => {
  const _a = applyCommonProps(props, SideNavigationItem, true),
        {
    className,
    children,
    leading,
    isCurrent = false,
    target
  } = _a,
        rest = __rest(_a, ["className", "children", "leading", "isCurrent", "target"]);

  return jsxs("a", Object.assign({
    className: classNames(style$c.sideNavigationItem, isCurrent && style$c.isCurrent, className),
    ref: ref
  }, isCurrent && {
    "aria-current": "page"
  }, getTarget(target), rest, {
    children: [leading && jsx("span", Object.assign({
      className: style$c.leading
    }, {
      children: leading
    }), void 0), children]
  }), void 0);
});
SideNavigationItem.displayName = "SideNavigationItem";

var style$b = {"skeleton":"Skeleton-module_skeleton__2gsll","rectangle":"Skeleton-module_rectangle__2tiL-","rounded":"Skeleton-module_rounded__2lQse"};

/**
 * A skeleton is a low-fidelity shape that approximates a user interface element and indicates loading of content. A group of skeletons roughly matching a loaded screen can improve perceived responsiveness when loading data is slow.
 *
 * @see {@link https://livingdesign.walmart.com/components/skeleton/react/}
 */

const Skeleton = props => {
  const _a = applyCommonProps(props, Skeleton, true),
        {
    className,
    height,
    style: styleProp,
    variant = "rectangle",
    width
  } = _a,
        rest = __rest(_a, ["className", "height", "style", "variant", "width"]);

  return jsx("div", Object.assign({
    className: classNames(style$b.skeleton, variant === "rectangle" && style$b.rectangle, variant === "rounded" && style$b.rounded, className),
    style: Object.assign(Object.assign({}, styleProp), {
      height,
      width
    })
  }, rest), void 0);
};
Skeleton.displayName = "Skeleton";

var style$a = {"skeletonText":"SkeletonText-module_skeletonText__3a6t-","skeleton":"SkeletonText-module_skeleton__22gnQ"};

/**
 * @see {@link https://livingdesign.walmart.com/components/skeleton/react/#skeleton-text}
 */

const SkeletonText = props => {
  const _a = applyCommonProps(props, SkeletonText, true),
        {
    className,
    lines = 3
  } = _a,
        rest = __rest(_a, ["className", "lines"]);

  return jsx("div", Object.assign({
    className: classNames(style$a.skeletonText, className)
  }, rest, {
    children: Array.from(new Array(lines)).map((_, index) => jsx(Skeleton, {
      UNSAFE_className: style$a.skeleton
    }, index))
  }), void 0);
};
SkeletonText.displayName = "SkeletonText";

var style$9 = {"container":"Spinner-module_container__2h3Ei","gray":"Spinner-module_gray__2i_zu","white":"Spinner-module_white__1LMxt","large":"Spinner-module_large__76y76","small":"Spinner-module_small__1e7K8","pill":"Spinner-module_pill__16WZJ","pill2":"Spinner-module_pill2__3l98g","pill3":"Spinner-module_pill3__9_hzy","pill4":"Spinner-module_pill4__3L8Ql","pill5":"Spinner-module_pill5__2onJX","pill6":"Spinner-module_pill6__31La9"};

/**
 * Spinners visually indicate that a process is taking place for an indeterminate amount of time.
 *
 * @see {@link https://livingdesign.walmart.com/components/spinner/react/}
 */

const Spinner = props => {
  const _a = applyCommonProps(props, Spinner, true),
        {
    a11yLabel = "Loading…",
    className,
    color = "gray",
    size = "large",
    spinnerProps = {}
  } = _a,
        rest = __rest(_a, ["a11yLabel", "className", "color", "size", "spinnerProps"]);

  const uniqueTitleId = useSSRSafeId();
  return jsx("span", Object.assign({
    className: classNames(style$9.container, size === "large" && style$9.large, size === "small" && style$9.small, className),
    role: "status"
  }, rest, {
    children: jsxs("svg", Object.assign({
      "aria-labelledby": uniqueTitleId,
      className: classNames(color === "gray" && style$9.gray, color === "white" && style$9.white),
      role: "img",
      viewBox: "0 0 48 48",
      xmlns: "http://www.w3.org/2000/svg"
    }, spinnerProps, {
      children: [jsx("title", Object.assign({
        id: uniqueTitleId
      }, {
        children: a11yLabel
      }), void 0), jsx("defs", {
        children: jsx("path", {
          d: "m22.1 15.967-.97-10.988C21.13 3.892 22.4 3 23.998 3c1.601 0 2.873.892 2.873 1.979L25.9 15.967c-.11.662-.918 1.173-1.902 1.173-.98 0-1.789-.511-1.897-1.173z",
          id: "ld-spinner-pill"
        }, void 0)
      }, void 0), jsx("use", {
        className: style$9.pill,
        href: "#ld-spinner-pill"
      }, void 0), jsx("use", {
        className: classNames(style$9.pill, style$9.pill2),
        href: "#ld-spinner-pill"
      }, void 0), jsx("use", {
        className: classNames(style$9.pill, style$9.pill3),
        href: "#ld-spinner-pill"
      }, void 0), jsx("use", {
        className: classNames(style$9.pill, style$9.pill4),
        href: "#ld-spinner-pill"
      }, void 0), jsx("use", {
        className: classNames(style$9.pill, style$9.pill5),
        href: "#ld-spinner-pill"
      }, void 0), jsx("use", {
        className: classNames(style$9.pill, style$9.pill6),
        href: "#ld-spinner-pill"
      }, void 0)]
    }), void 0)
  }), void 0);
};
Spinner.displayName = "Spinner";

var style$8 = {"spotIcon":"SpotIcon-module_spotIcon__3Cu5u","blue":"SpotIcon-module_blue__3-aco","white":"SpotIcon-module_white__3IDy4","large":"SpotIcon-module_large__3Rw_I","small":"SpotIcon-module_small__3bTBG"};

const SpotIcon = props => {
  const _a = applyCommonProps(props, SpotIcon),
        {
    children,
    className,
    color = "blue",
    size = "small"
  } = _a,
        rest = __rest(_a, ["children", "className", "color", "size"]);

  const child = React.Children.only(children);
  return jsx("div", Object.assign({
    className: classNames(style$8.spotIcon, color === "blue" && style$8.blue, color === "white" && style$8.white, size === "small" && style$8.small, size === "large" && style$8.large, className)
  }, rest, {
    children: React.cloneElement(child, {
      size: {
        small: "medium",
        large: "large"
      }[size]
    })
  }), void 0);
};

var style$7 = {"styledText":"StyledText-module_styledText__28SSQ","blue":"StyledText-module_blue__3wzVn","gray":"StyledText-module_gray__2hQ6z","green":"StyledText-module_green__3PI_y","leading":"StyledText-module_leading__1MG6l","small":"StyledText-module_small__3DYNM","large":"StyledText-module_large__1nxQO"};

/**
 * Styled text visually enhances copy and can help convey meaning by adding color and/or an optional icon.
 *
 * @see {@link https://livingdesign.walmart.com/components/styled-text/react/}
 */

const StyledText = props => {
  const _a = applyCommonProps(props, StyledText, true),
        {
    children,
    className,
    color = "gray",
    leading,
    size = "small"
  } = _a,
        rest = __rest(_a, ["children", "className", "color", "leading", "size"]);

  return jsxs("span", Object.assign({
    className: classNames(style$7.styledText, className, color === "blue" && style$7.blue, color === "gray" && style$7.gray, color === "green" && style$7.green, size === "large" && style$7.large, size === "small" && style$7.small)
  }, rest, {
    children: [leading && jsx("span", Object.assign({
      className: style$7.leading
    }, {
      children: leading
    }), void 0), children]
  }), void 0);
};
StyledText.displayName = "StyledText";

var style$6 = {"pill":"Switch-module_pill__4YwXM","indicator":"Switch-module_indicator__3lflQ","on":"Switch-module_on__2vyy8","hasLabel":"Switch-module_hasLabel__20Ws6","label":"Switch-module_label__QHtV_"};

/**
 * Switches are generally used to activate or deactivate (on/off) a specific setting.
 *
 * @see {@link https://livingdesign.walmart.com/components/switch/react/}
 */

const Switch = React.forwardRef((props, ref) => {
  const _a = applyCommonProps(props, Switch),
        {
    a11yLabelledBy,
    className,
    isOn = false,
    disabled = false,
    label,
    onClick
  } = _a,
        rest = __rest(_a, ["a11yLabelledBy", "className", "isOn", "disabled", "label", "onClick"]);

  const labelCount = (label ? 1 : 0) + (a11yLabelledBy ? 1 : 0) === 1;
  invariant(labelCount, "`Switch` accessibility violation. `Switch` requires a `label` OR an `a11yLabelledBy`.");
  return jsxs("button", Object.assign({
    "aria-checked": isOn,
    className: classNames(style$6.pill, isOn && style$6.on, className),
    disabled: disabled,
    onClick: onClick,
    ref: ref,
    role: "switch",
    type: "button"
  }, a11yLabelledBy && {
    "aria-labelledby": a11yLabelledBy
  }, rest, {
    children: [jsx("span", {
      className: classNames(style$6.indicator, isOn && style$6.on, label && style$6.hasLabel)
    }, void 0), label && jsx("span", Object.assign({
      className: classNames(style$6.label, isOn && style$6.on)
    }, {
      children: label
    }), void 0)]
  }), void 0);
});
Switch.displayName = "Switch";

var style$5 = {"tabNavigation":"TabNavigation-module_tabNavigation__3Z1sY","list":"TabNavigation-module_list__1XjYW","listItem":"TabNavigation-module_listItem__29Khp"};

/**
 * Tab Navigation allows for page-level navigation between sets of content, with a selected state, typically used at the top of the screen.
 *
 * @see {@link https://livingdesign.walmart.com/components/tab-navigation/react/}
 */

const TabNavigation = props => {
  const _a = applyCommonProps(props, TabNavigation),
        {
    children,
    className
  } = _a,
        rest = __rest(_a, ["children", "className"]);

  return jsx("nav", Object.assign({
    className: classNames(style$5.tabNavigation, className)
  }, rest, {
    children: jsx("ul", Object.assign({
      className: style$5.list
    }, {
      children: React.Children.map(children, (child, index) => jsx("li", Object.assign({
        className: style$5.listItem
      }, {
        children: child
      }), index))
    }), void 0)
  }), void 0);
};
TabNavigation.displayName = "TabNavigation";

var style$4 = {"tabNavigationItem":"TabNavigationItem-module_tabNavigationItem__2YjeP","tabContent":"TabNavigationItem-module_tabContent__3jAwf","isCurrent":"TabNavigationItem-module_isCurrent__20aAc"};

/**
 * @see {@link https://livingdesign.walmart.com/components/tab-navigation/react/}
 */

const TabNavigationItem = React.forwardRef((props, ref) => {
  const _a = applyCommonProps(props, TabNavigationItem, true),
        {
    className,
    children,
    isCurrent = false,
    target
  } = _a,
        rest = __rest(_a, ["className", "children", "isCurrent", "target"]);

  return jsx("a", Object.assign({
    className: classNames(style$4.tabNavigationItem, isCurrent && style$4.isCurrent, className),
    ref: ref
  }, isCurrent && {
    "aria-current": "page"
  }, getTarget(target), rest, {
    children: jsx("span", Object.assign({
      className: style$4.tabContent
    }, {
      children: children
    }), void 0)
  }), void 0);
});
TabNavigationItem.displayName = "TabNavigationItem";

var style$3 = {"tag":"Tag-module_tag__3t5NV","leading":"Tag-module_leading__hWl5p","primary":"Tag-module_primary__378AE","red":"Tag-module_red__1Ks6U","spark":"Tag-module_spark__3UUit","green":"Tag-module_green__3GSl8","blue":"Tag-module_blue__24bf6","purple":"Tag-module_purple__2xups","gray":"Tag-module_gray__3vMRU","secondary":"Tag-module_secondary__17MMS","tertiary":"Tag-module_tertiary__1plwI"};

/**
 * Tags are used to draw a customer’s focus to item traits such as availability, status, or media rating. Items may be products, fulfillment slots, or something else. They are visual only and non-interactive.
 *
 * @see {@link https://livingdesign.walmart.com/components/tag/react/}
 */

const Tag = props => {
  const _a = applyCommonProps(props, Tag, true),
        {
    children,
    className,
    color = "blue",
    leading,
    variant = "secondary"
  } = _a,
        rest = __rest(_a, ["children", "className", "color", "leading", "variant"]);

  return jsxs("span", Object.assign({
    className: classNames(style$3.tag, color === "red" && style$3.red, color === "spark" && style$3.spark, color === "green" && style$3.green, color === "blue" && style$3.blue, color === "purple" && style$3.purple, color === "gray" && style$3.gray, variant === "primary" && style$3.primary, variant === "secondary" && style$3.secondary, variant === "tertiary" && style$3.tertiary, className)
  }, rest, {
    children: [leading && jsx("span", Object.assign({
      className: style$3.leading
    }, {
      children: leading
    }), void 0), children]
  }), void 0);
};
Tag.displayName = "Tag";

var style$2 = {"container":"TextArea-module_container__1C8Wh","textarea":"TextArea-module_textarea__28FqA","error":"TextArea-module_error__35H1Z","helperText":"TextArea-module_helperText__XIK89","maxLength":"TextArea-module_maxLength__2pWGq"};

/**
 * Debounce calls to a function.
 * @private
 *
 * @param {Function} fn The function to call
 * @param {number} timeout The time to debounce calls
 * @returns {Function} The debounced function
 */
const debounce = (fn, timeout) => {
  let timerID;

  const cancel = () => {
    clearTimeout(timerID);
  };

  const debounced = (...args) => {
    clearTimeout(timerID);
    timerID = setTimeout(() => {
      fn(...args);
    }, timeout);
  };

  debounced.cancel = cancel;
  return debounced;
};

/**
 * Text areas allow for the input of freeform, often multi-line, text.
 *
 * @see {@link https://livingdesign.walmart.com/components/text-area/react/}
 */

const TextArea = React.forwardRef((props, ref) => {
  const _a = applyCommonProps(props, TextArea, true),
        {
    className,
    disabled = false,
    error,
    helperText,
    id: initialId,
    label,
    maxLength,
    onChange,
    placeholder,
    textAreaProps: initialTextAreaProps,
    value = ""
  } = _a,
        rest = __rest(_a, ["className", "disabled", "error", "helperText", "id", "label", "maxLength", "onChange", "placeholder", "textAreaProps", "value"]);

  const [isFocused, setIsFocused] = React.useState(false);
  const [screenReaderMessage, setScreenReaderMessage] = React.useState("");
  const id = useSSRSafeId(initialId);

  const _b = initialTextAreaProps || {},
        {
    className: initialClassName,
    onBlur: initialOnBlur,
    onFocus: initialOnFocus
  } = _b,
        textAreaRest = __rest(_b, ["className", "onBlur", "onFocus"]);

  const helperId = `${id}_helper`;
  /* eslint-disable-next-line react-hooks/exhaustive-deps */

  const setDelayedScreenReaderMessage = React.useMemo(() => debounce(setScreenReaderMessage, 1500), []);
  React.useEffect(() => {
    if (!maxLength) {
      return () => undefined;
    }

    const message = `${maxLength - value.length} characters left.`;
    setDelayedScreenReaderMessage(message);
    return () => {
      setDelayedScreenReaderMessage.cancel();
    };
  }, [maxLength, setDelayedScreenReaderMessage, value]);
  return jsxs("div", Object.assign({
    className: classNames(style$2.container, className)
  }, rest, {
    children: [jsx(FormLabel, Object.assign({
      disabled: disabled,
      htmlFor: id,
      isEmpty: !placeholder && !value,
      isFocused: isFocused
    }, {
      children: label
    }), void 0), jsx("textarea", Object.assign({
      className: classNames(style$2.textarea, error && style$2.error, initialClassName),
      disabled: disabled,
      id: id,
      maxLength: maxLength,
      onBlur: event => {
        setIsFocused(false);
        initialOnBlur === null || initialOnBlur === void 0 ? void 0 : initialOnBlur(event);
      },
      onChange: onChange,
      onFocus: event => {
        setIsFocused(true);
        initialOnFocus === null || initialOnFocus === void 0 ? void 0 : initialOnFocus(event);
      },
      placeholder: placeholder,
      ref: ref,
      value: value
    }, (error || helperText || maxLength) && {
      "aria-describedby": helperId
    }, textAreaRest), void 0), jsxs("div", Object.assign({
      id: helperId
    }, {
      children: [(error || helperText || maxLength) && jsxs(FormHelperText, Object.assign({
        className: style$2.helperText
      }, {
        children: [error && jsx(FormError, {
          children: error
        }, void 0), !error && jsx("span", {
          children: helperText
        }, void 0), maxLength && jsx("span", Object.assign({
          className: style$2.maxLength
        }, {
          children: jsxs("span", Object.assign({
            "aria-hidden": true
          }, {
            children: [value.length, " / ", maxLength]
          }), void 0)
        }), void 0)]
      }), void 0), jsx(VisuallyHidden, Object.assign({
        "aria-atomic": true,
        "aria-live": "polite"
      }, {
        children: screenReaderMessage
      }), void 0)]
    }), void 0)]
  }), void 0);
});
TextArea.displayName = "TextArea";

var style$1 = {"container":"TextField-module_container__24LW8","label":"TextField-module_label__WejTT","isFocused":"TextField-module_isFocused__wblyi","inputContainer":"TextField-module_inputContainer__28aDe","error":"TextField-module_error__3d2nE","isDisabled":"TextField-module_isDisabled__3HbuB","input":"TextField-module_input__15sq2","leadingIcon":"TextField-module_leadingIcon__2YTZW","icon":"TextField-module_icon__1wTBM","trailing":"TextField-module_trailing__1Eetb"};

const autoComplete = {
  email: "email",
  password: "new-password",
  tel: "tel"
};
/**
 * Text fields allow users to enter and edit text.
 *
 * @see {@link https://livingdesign.walmart.com/components/text-field/react/}
 */

const TextField = React.forwardRef((props, ref) => {
  const _a = applyCommonProps(props, TextField, true),
        {
    className,
    disabled = false,
    error,
    helperText,
    id: initialId,
    label,
    leadingIcon: initialLeadingIcon,
    onChange,
    placeholder,
    textFieldProps: initialTextFieldProps,
    trailing,
    type,
    value = ""
  } = _a,
        rest = __rest(_a, ["className", "disabled", "error", "helperText", "id", "label", "leadingIcon", "onChange", "placeholder", "textFieldProps", "trailing", "type", "value"]);

  const [isFocused, setIsFocused] = React.useState(false);
  const id = useSSRSafeId(initialId);
  const helperId = `${id}_helper`;
  const textFieldProps = Object.assign(Object.assign(Object.assign({
    disabled,
    id,
    onChange,
    placeholder,
    type,
    value,
    autoComplete: type && autoComplete[type]
  }, (error || helperText) && {
    "aria-describedby": helperId
  }), initialTextFieldProps), {
    className: classNames(style$1.input, initialTextFieldProps === null || initialTextFieldProps === void 0 ? void 0 : initialTextFieldProps.className),

    onBlur(event) {
      var _a;

      setIsFocused(false);
      (_a = initialTextFieldProps === null || initialTextFieldProps === void 0 ? void 0 : initialTextFieldProps.onBlur) === null || _a === void 0 ? void 0 : _a.call(initialTextFieldProps, event);
    },

    onFocus(event) {
      var _a;

      setIsFocused(true);
      (_a = initialTextFieldProps === null || initialTextFieldProps === void 0 ? void 0 : initialTextFieldProps.onFocus) === null || _a === void 0 ? void 0 : _a.call(initialTextFieldProps, event);
    }

  });
  const leadingIcon = initialLeadingIcon && React.cloneElement(initialLeadingIcon, {
    size: "medium"
  });
  return jsxs("div", Object.assign({
    className: classNames(style$1.container, className)
  }, rest, {
    children: [jsx(FormLabel, Object.assign({
      className: classNames(!isFocused && !placeholder && !value && leadingIcon && style$1.label),
      disabled: disabled,
      htmlFor: id,
      isEmpty: !placeholder && !value,
      isFocused: isFocused
    }, {
      children: label
    }), void 0), jsxs("div", Object.assign({
      className: classNames(style$1.inputContainer, disabled && style$1.isDisabled, error && style$1.error, isFocused && style$1.isFocused)
    }, {
      children: [leadingIcon && jsx("span", Object.assign({
        "aria-hidden": true,
        className: classNames(style$1.icon, style$1.leadingIcon)
      }, {
        children: leadingIcon
      }), void 0), jsx("input", Object.assign({}, textFieldProps, {
        ref: ref
      }), void 0), trailing && jsx("span", Object.assign({
        className: classNames(style$1.icon, style$1.trailing)
      }, {
        children: trailing
      }), void 0)]
    }), void 0), (error || helperText) && jsxs(FormHelperText, Object.assign({
      id: helperId
    }, {
      children: [error && jsx(FormError, {
        children: error
      }, void 0), !error && jsx("span", {
        children: helperText
      }, void 0)]
    }), void 0)]
  }), void 0);
});
TextField.displayName = "TextField";

var style = {"wizardFooter":"WizardFooter-module_wizardFooter__3Uf-_","wizardContent":"WizardFooter-module_wizardContent__wIY0p","wizardPrevious":"WizardFooter-module_wizardPrevious__3A_HE","wizardNext":"WizardFooter-module_wizardNext__3mXCf"};

const WizardFooter = props => {
  const {
    children,
    nextActionProps,
    previousActionProps
  } = props;
  return jsxs("div", Object.assign({
    className: style.wizardFooter
  }, {
    children: [children && jsx("div", Object.assign({
      className: style.wizardContent
    }, {
      children: children
    }), void 0), jsx("div", Object.assign({
      className: style.wizardPrevious
    }, {
      children: jsx(Button, Object.assign({
        children: "Previous"
      }, previousActionProps, {
        size: "medium",
        variant: "secondary"
      }), void 0)
    }), void 0), jsx("div", Object.assign({
      className: style.wizardNext
    }, {
      children: jsx(Button, Object.assign({
        children: "Continue"
      }, nextActionProps, {
        size: "medium",
        variant: "primary"
      }), void 0)
    }), void 0)]
  }), void 0);
};

export { A11yAnnouncementContext, A11yAnnouncementProvider, Alert, BOGLE_BASELINE_RATIO, Badge, Banner, Body, BottomSheet, OverlayCloseButton as BottomSheetCloseButton, Breadcrumb, BreadcrumbItem, Button, ButtonGroup, Callout, Caption, Card, CardItem, Checkbox, Chip, ChipGroup, Collapse, Container, DataTable, DataTableBody, DataTableCellAlphaNumeric, DataTableCellNumeric, DataTableHead, DataTableHeader, DataTableRow, Display, Divider, FocusTrap, FormGroup, Grid, GridColumn, Heading, IconButton, LineClamp, Link, List, ListItem, LivingDesignProvider, Modal, Nudge, PageTitle, Panel, OverlayCloseButton as PanelCloseButton, Popover, ProgressIndicator, ProgressTracker, ProgressTrackerItem, Radio, Rating, Select, SideNavigation, SideNavigationItem, Skeleton, SkeletonText, SnackbarContext, SnackbarProvider, Spinner, SpotIcon, StyledText, Subheading, Switch, TabNavigation, TabNavigationItem, Tag, TextArea, TextField, VisuallyHidden, WizardFooter, useA11yAnnouncement, useCollapseTransition, useLockBodyScroll, useOnClick, useOnClickInside, useOnClickOutside, useOnKeyDown, useSnackbar };
